/**
 * @fileoverview Componente responsável por editar a configuração do nó ou do fluxo selecionado.
 */

import React from "react";
import { IconButton, Tooltip, CircularProgress } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import {
  Check, X, Info, Globe, Plus, Trash2, FileUp, Upload, Maximize2,
  Bot, Minimize2, Brain, Pencil, Sparkles, ChevronDown, ChevronUp,
  SquareKanban, RefreshCw, ArrowRightLeft, GripVertical, Send,
  ShoppingBag, AlertTriangle, BookOpen, SlidersHorizontal,
} from "lucide-react";

import { NODE_TYPES_CONFIG, NODE_ICON_COLOR, NODE_ICON_CHIP } from "../constants";
import BulkFilterPanel from "./BulkFilterPanel";
import BulkBasesPanel from "./BulkBasesPanel";
import CustomDatePicker from "./CustomDatePicker";
import MassSendMonitor from "./MassSendMonitor";
import TemplateVariableMapper from "./TemplateVariableMapper";
import OrderStatusPanel from "./OrderStatusPanel";
import OrderTemplateVariables, { initialOrderVariables, orderTemplateProblem } from "./OrderTemplateVariables";
import { upstreamNode } from "../flowGraph";
import AiToolsGuideModal from "../components/AiToolsGuideModal";
import MassSendConfirmModal from "../components/MassSendConfirmModal";
import useContactBases from "../../hooks/useContactBases";
import useMassSend from "../../hooks/useMassSend";
import usePermission, { isAdminUser } from "../../../../hooks/usePermission";
import { AuthContext } from "../../../../context/Auth/AuthContext";
import { formatRelativeTime } from "../../../../utils/formatRelativeTime";
import { decodeCsvText } from "../../../../utils/decodeCsvText";
import { AI_AGENT_MODEL_GROUPS, AI_AGENT_MODELS, AI_AGENT_REASONING_LEVELS } from "../../aiAgentOptions";
import AgentPicker, { AgentOpenButton } from "./AgentPicker";
import { KNOWLEDGE_TOOL_TYPES, agentNodeMode } from "../agentNode";

// Sem permissão para editar o fluxo, o painel só mostra: o conteúdo fica inerte
// (sem clique, foco nem digitação) e a rolagem continua no corpo. O que não é
// editar o fluxo (live: o acompanhamento do disparo) fica fora da parte inerte.
var ConfigBody = function (props) {
  var classes = props.classes;
  return (
    <div className={classes.configBody}>
      {props.readOnly && <div className={classes.readOnlyNote}>Você pode ver este fluxo, mas não editá-lo.</div>}
      {props.readOnly && props.live && <div className={classes.configField}>{props.live}</div>}
      <div inert={props.readOnly ? "" : undefined}>{props.children}</div>
    </div>
  );
};

/**
 * Config panel (right side) for editing selected node properties.
 */
var ConfigPanel = function (props) {
  var classes = props.classes;
  // Suporte a modo claro: cores brancas-com-alpha (usadas nos estilos inline)
  // viram escuras-com-mesmo-alpha no tema claro. O espelhamento é correto para
  // texto, borda e fundo sutil — no dark o visual fica idêntico ao original.
  var isDark = useTheme().palette.type === "dark";
  var W = function (a) {
    return isDark ? "rgba(255,255,255," + a + ")" : "rgba(20,22,30," + a + ")";
  };
  var PANEL = isDark ? PANEL : "#ffffff"; // fundo de popovers/dropdowns customizados
  var aiGuideState = React.useState(false);
  var guideOpen = aiGuideState[0];
  var setGuideOpen = aiGuideState[1];

  // Dropdown customizado do seletor de modelo (substitui o <select> nativo,
  // que abre com fundo branco do browser e destoa do tema dark).
  var modelDropdownState = React.useState(false);
  var modelDropdownOpen = modelDropdownState[0];
  var setModelDropdownOpen = modelDropdownState[1];

  var aiUsersState = React.useState([]);
  var aiUsers = aiUsersState[0];
  var setAiUsers = aiUsersState[1];

  var aiUsersLoadingState = React.useState(false);
  var aiUsersLoading = aiUsersLoadingState[0];
  var setAiUsersLoading = aiUsersLoadingState[1];

  React.useEffect(function () {
    if (!props.selectedNode || props.selectedNode.data.nodeType !== "ai_agent") return;
    var cancelled = false;
    setAiUsersLoading(true);
    props.api
      .get("/users/list?onlyAi=true")
      .then(function (resp) {
        if (cancelled) return;
        setAiUsers(Array.isArray(resp.data) ? resp.data : []);
      })
      .catch(function () {
        if (!cancelled) setAiUsers([]);
      })
      .finally(function () {
        if (!cancelled) setAiUsersLoading(false);
      });
    return function () { cancelled = true; };
  }, [props.selectedNode && props.selectedNode.id]);

  // ── Triagens publicadas (node "Triagem IA") ──
  var triagesState = React.useState(null);
  var triages = triagesState[0];
  var setTriages = triagesState[1];
  React.useEffect(function () {
    if (!props.selectedNode || props.selectedNode.data.nodeType !== "ai_triage") return;
    var cancelled = false;
    setTriages(null);
    props.api
      .get("/triage-definitions/available")
      .then(function (resp) {
        if (!cancelled) setTriages(Array.isArray(resp.data) ? resp.data : []);
      })
      .catch(function () {
        if (!cancelled) setTriages([]);
      });
    return function () { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedNode && props.selectedNode.id]);

  // ── Bases de contatos (seletor "Lista salva" + mapeamento por coluna) ──
  var basesApi = useContactBases({ autoLoad: false });
  var contactBases = basesApi.bases;
  React.useEffect(function () {
    var sn = props.selectedNode;
    if (!sn || !sn.data) return;
    var t = sn.data.nodeType;
    if (t === "bulk_message" || t === "send_template") basesApi.list();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedNode && props.selectedNode.id]);

  // ── Disparo em massa durável (validação + monitoramento ao vivo) ──
  var massSend = useMassSend(props.workflowId);
  // Disparar, pausar, continuar, cancelar e mudar o máximo por dia pedem
  // mass_send.manage, além de editar o fluxo.
  var canMassSend = usePermission().can("mass_send.manage", true);
  // Agentes de IA (bloco "Agente IA"): ver abre o "Gerenciar Agentes"; criar,
  // editar e transformar o bloco em agente pedem ai_agents.manage.
  var authUser = React.useContext(AuthContext).user;
  var canViewAgents = usePermission().can("ai_agents.view", isAdminUser(authUser));
  var canManageAgents = usePermission().can("ai_agents.manage", isAdminUser(authUser));
  React.useEffect(function () {
    var sn = props.selectedNode;
    if (!sn || !sn.data || !props.workflowId) return;
    if (sn.data.nodeType === "bulk_message") massSend.fetchLatest(props.workflowId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedNode && props.selectedNode.id, props.workflowId]);

  var confirmOpenState = React.useState(false);
  var confirmOpen = confirmOpenState[0];
  var setConfirmOpen = confirmOpenState[1];
  var validatingState = React.useState(false);
  var validating = validatingState[0];
  var setValidating = validatingState[1];
  var validationState = React.useState(null);
  var validation = validationState[0];
  var setValidation = validationState[1];
  var dispatchingState = React.useState(false);
  var dispatching = dispatchingState[0];
  var setDispatching = dispatchingState[1];
  var advancedOpenState = React.useState(false);
  var advancedOpen = advancedOpenState[0];
  var setAdvancedOpen = advancedOpenState[1];
  var massActionBusyState = React.useState(false);
  var massActionBusy = massActionBusyState[0];
  var setMassActionBusy = massActionBusyState[1];

  var selectedNode = props.selectedNode;
  var setSelectedNodeId = props.setSelectedNodeId;
  var updateNodeData = props.updateNodeData;
  var queues = props.queues;
  var whatsapps = props.whatsapps;
  var setPromptModalOpen = props.setPromptModalOpen;
  var fileUploading = props.fileUploading;
  var setFileUploading = props.setFileUploading;
  var pendingFileName = props.pendingFileName;
  var setPendingFileName = props.setPendingFileName;
  var pendingFile = props.pendingFile;
  var setPendingFile = props.setPendingFile;
  var toastError = props.toastError;
  var api = props.api;
  var memName = props.memName;
  var setMemName = props.setMemName;
  var memContent = props.memContent;
  var setMemContent = props.setMemContent;
  var memSummary = props.memSummary;
  var setMemSummary = props.setMemSummary;
  var memGenerating = props.memGenerating;
  var setMemGenerating = props.setMemGenerating;
  var memEditingId = props.memEditingId;
  var setMemEditingId = props.setMemEditingId;
  var memExpandedId = props.memExpandedId;
  var setMemExpandedId = props.setMemExpandedId;
  var leadStatuses = props.leadStatuses || [];
  var setLeadStatuses = props.setLeadStatuses;
  var crmBoards = props.crmBoards || [];
  var crmFieldName = props.crmFieldName;
  var setCrmFieldName = props.setCrmFieldName;
  var setMemPromptModalOpen = props.setMemPromptModalOpen;
  var users = props.users || [];
  var edges = props.edges || [];
  var nodes = props.nodes || [];
  var wooIntegrations = props.wooIntegrations || [];

  if (!selectedNode) return null;

  var cfg = NODE_TYPES_CONFIG.find(function (n) { return n.type === selectedNode.data.nodeType; }) || NODE_TYPES_CONFIG[0];
  var Icon = cfg.icon;
  var nodeData = selectedNode.data;
  var selectedQueues = nodeData.queues || [];
  var selectedWhatsapps = nodeData.whatsapps || [];
  var allConnections = nodeData.allConnections === true;

  // Bloco "Agente IA": com agente, o texto, as fontes e a inteligência vêm dele
  // (os campos do "Personalizado" somem, e os arquivos, casos, pesquisa e loja
  // ligados ao bloco ficam sem uso).
  var agentMode = agentNodeMode(nodeData);
  var hasKnowledgeTools = cfg.type === "ai_agent" && edges.some(function (e) {
    if (e.source !== selectedNode.id || e.sourceHandle !== "tools") return false;
    var tool = nodes.find(function (n) { return n.id === e.target; });
    return !!(tool && KNOWLEDGE_TOOL_TYPES.indexOf(tool.data.nodeType) !== -1);
  });

  var toggleQueue = function (queueId) {
    var current = nodeData.queues || [];
    var idx = current.indexOf(queueId);
    if (idx === -1) {
      updateNodeData("queues", current.concat(queueId));
    } else {
      updateNodeData("queues", current.filter(function (id) { return id !== queueId; }));
    }
  };

  var toggleWhatsapp = function (waId) {
    var current = nodeData.whatsapps || [];
    var idx = current.indexOf(waId);
    if (idx === -1) {
      updateNodeData("whatsapps", current.concat(waId));
    } else {
      updateNodeData("whatsapps", current.filter(function (id) { return id !== waId; }));
    }
  };

  /* ── Disparo em massa: schedule/dedup + confirmação + monitoramento ── */
  var bulkSchedule = nodeData.schedule || {};
  var bulkDedup = nodeData.dedup || {};
  var updateBulkSchedule = function (patch) {
    updateNodeData("schedule", Object.assign({}, bulkSchedule, patch));
  };
  var updateBulkDedup = function (patch) {
    updateNodeData("dedup", Object.assign({}, bulkDedup, patch));
  };

  var getConnectedTemplateNode = function () {
    var tplEdge = (props.edges || []).find(function (e) { return e.source === selectedNode.id; });
    if (!tplEdge) return null;
    return (props.nodes || []).find(function (n) {
      return n.id === tplEdge.target && n.data && n.data.nodeType === "send_template";
    }) || null;
  };

  var getSelectedBases = function () {
    var ids = nodeData.baseIds || [];
    var map = {};
    (contactBases || []).forEach(function (b) { map[b.id] = b; });
    return ids.map(function (id) { return map[id] || { id: id }; });
  };

  var buildBulkValidatePayload = function () {
    var tplNode = getConnectedTemplateNode();
    var tpl = (tplNode && tplNode.data) || {};
    var sel = tpl.selectedTemplate || {};
    return {
      whatsappId: nodeData.whatsappId,
      templateName: tpl.templateName || null,
      language: sel.language || null,
      bodyText: sel.bodyText || "",
      variableMapping: tpl.variableMapping || {},
      source: "bases",
      baseIds: nodeData.baseIds || [],
      // Datas do agendamento validam no backend (endDate no passado etc.).
      schedule: nodeData.schedule || {},
    };
  };

  var openBulkConfirm = function () {
    setConfirmOpen(true);
    setValidating(true);
    setValidation(null);
    massSend
      .validate(buildBulkValidatePayload())
      .then(function (data) { setValidation(data); })
      .catch(function (err) {
        var msg = (err && err.response && err.response.data && err.response.data.error) || "Falha ao validar o disparo.";
        setValidation({ valid: false, errors: [msg], estimatedContacts: null, sample: null });
      })
      .finally(function () { setValidating(false); });
  };

  var handleConfirmDispatch = function () {
    setDispatching(true);
    // Aguarda o disparo (save + POST) resolver — mantém o spinner e só fecha o
    // modal ao concluir. handleBulkSend trata o erro (toast) e sempre resolve.
    Promise.resolve(props.handleBulkSend ? props.handleBulkSend() : null)
      .then(function () {
        setConfirmOpen(false);
        if (props.workflowId) {
          setTimeout(function () { massSend.fetchLatest(props.workflowId); }, 1500);
        }
      })
      .finally(function () { setDispatching(false); });
  };

  var runMassAction = function (action) {
    var exec = massSend.execution;
    if (!exec) return;
    if (action === "cancel" && !window.confirm("Cancelar este disparo? Os contatos pendentes não serão enviados.")) return;
    var fn = action === "pause" ? massSend.pause : action === "resume" ? massSend.resume : massSend.cancel;
    setMassActionBusy(true);
    fn(exec.id)
      .catch(function (err) { if (props.toastError) props.toastError(err); })
      .finally(function () { setMassActionBusy(false); });
  };

  // Edita o máximo por dia da execução em andamento (o modal só afeta disparos
  // novos). Retorna a promise para o MassSendMonitor controlar o "salvando".
  var runMassUpdateCap = function (cap) {
    var exec = massSend.execution;
    if (!exec) return Promise.resolve();
    return massSend.updateExecution(exec.id, { maxSendsPerDay: cap })
      .catch(function (err) { if (props.toastError) props.toastError(err); throw err; });
  };

  // O acompanhamento do disparo por bases: abrir os destinatários pede
  // mass_send.view (o hook só busca a execução com ela) e pausar, continuar,
  // cancelar e mudar o máximo por dia, mass_send.manage. Com o fluxo só para
  // ver, ele vai para o topo do painel, fora da parte inerte.
  var massMonitor = cfg.type === "bulk_message" && nodeData.source === "bases" && massSend.execution ? (
    <MassSendMonitor
      execution={massSend.execution}
      busy={massActionBusy}
      fetchRecipients={massSend.fetchRecipients}
      {...(canMassSend ? {
        onPause: function () { runMassAction("pause"); },
        onResume: function () { runMassAction("resume"); },
        onCancel: function () { runMassAction("cancel"); },
        onUpdateCap: runMassUpdateCap,
      } : {})}
    />
  ) : null;

  // Só ver o fluxo: o painel fica inerte, e o agente do bloco abre por aqui, fora
  // da parte inerte (abrir o agente não é editar o fluxo).
  var viewedAgent = cfg.type === "ai_agent" && props.readOnly && canViewAgents && agentMode === "agent"
    ? (props.agentOptions || []).find(function (agent) { return agent.id === Number(nodeData.agentId); }) || null
    : null;
  var agentLive = viewedAgent ? (
    <AgentOpenButton
      classes={classes}
      canManageAgents={canManageAgents}
      onClick={function () { props.openAgentManager({ nodeId: selectedNode.id, agentId: viewedAgent.id }); }}
    />
  ) : null;

  return (
    <div
      className={classes.configOverlay}
      onClick={function () { setSelectedNodeId(null); }}
    >
      <div
        className={classes.configPanel}
        onClick={function (e) { e.stopPropagation(); }}
      >
      <div className={classes.configHeader}>
        <div
          className={classes.configHeaderIcon}
          style={{ background: NODE_ICON_CHIP(isDark) }}
        >
          <Icon size={16} color={NODE_ICON_COLOR(isDark)} strokeWidth={2} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={classes.configHeaderTitle}>{cfg.label}</div>
          <div className={classes.configHeaderDesc}>{cfg.desc}</div>
        </div>
        <IconButton
          size="small"
          className={classes.configClose}
          onClick={function () { setSelectedNodeId(null); }}
        >
          <X size={16} />
        </IconButton>
      </div>

      <ConfigBody classes={classes} readOnly={props.readOnly} live={massMonitor || agentLive}>
        {cfg.type === "receive_message" && (
          <React.Fragment>
            {/* Receber do setor */}
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Receber do setor</span>
                <Tooltip
                  title="O fluxo só atende as conversas dos setores marcados. Sem setor marcado, vale para qualquer setor."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>

              {selectedQueues.length > 0 && (
                <div className={classes.configChips}>
                  {selectedQueues.map(function (qId) {
                    var q = queues.find(function (queue) { return queue.id === qId; });
                    if (!q) return null;
                    return (
                      <span key={qId} className={classes.configChip}>
                        <span
                          className={classes.configDropdownColor}
                          style={{ background: q.color || "#5B7CFF" }}
                        />
                        {q.name}
                        <span
                          className={classes.configChipRemove}
                          onClick={function () { toggleQueue(qId); }}
                        >
                          <X size={10} />
                        </span>
                      </span>
                    );
                  })}
                </div>
              )}

              <div className={classes.configDropdown} style={{ marginTop: 6 }}>
                {queues.length === 0 && (
                  <div className={classes.configEmpty}>Nenhum setor encontrado</div>
                )}
                {queues.map(function (q) {
                  var isSelected = selectedQueues.indexOf(q.id) !== -1;
                  return (
                    <div
                      key={q.id}
                      className={classes.configDropdownItem}
                      onClick={function () { toggleQueue(q.id); }}
                    >
                      <div
                        className={
                          classes.configDropdownCheck +
                          (isSelected ? " " + classes.configDropdownCheckActive : "")
                        }
                      >
                        {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                      </div>
                      <span
                        className={classes.configDropdownColor}
                        style={{ background: q.color || "#5B7CFF" }}
                      />
                      {q.name}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Receber da conexão */}
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Receber da conexão</span>
                <Tooltip
                  title="O fluxo só atende as mensagens que chegam nas conexões marcadas."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>

              {/* "Todas as conexões" (bloco 3b dos planos): vale em qualquer número,
                  também nos conectados depois; o fluxo com a conexão marcada ganha dele. */}
              <div className={classes.configToggleRow} style={{ padding: "2px 0 8px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={classes.configLabel} style={{ color: W(0.85) }}>Todas as conexões</span>
                  <Tooltip
                    title="Atende em qualquer conexão da empresa, também nas que forem conectadas depois. Se outro fluxo tiver a conexão marcada, ele tem preferência."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={allConnections}
                  aria-label="Todas as conexões"
                  onClick={function () { updateNodeData("allConnections", !allConnections); }}
                  style={{
                    width: 36, height: 20, borderRadius: 10, cursor: "pointer", border: "none", padding: 0,
                    background: allConnections ? "#5B7CFF" : W(0.1),
                    position: "relative", transition: "background 0.2s", flexShrink: 0,
                  }}
                >
                  <span style={{
                    width: 16, height: 16, borderRadius: 8, background: "#fff",
                    position: "absolute", top: 2, transition: "left 0.2s",
                    left: allConnections ? 18 : 2
                  }} />
                </button>
              </div>

              {allConnections && (
                <div style={{ fontSize: 11, lineHeight: 1.45, color: W(0.55) }}>
                  Vale para todas as conexões, também as que forem conectadas depois.
                </div>
              )}

              {!allConnections && selectedWhatsapps.length > 0 && (
                <div className={classes.configChips}>
                  {selectedWhatsapps.map(function (waId) {
                    var wa = whatsapps.find(function (w) { return w.id === waId; });
                    if (!wa) return null;
                    return (
                      <span key={waId} className={classes.configChip}>
                        {wa.name}
                        <span
                          className={classes.configChipRemove}
                          onClick={function () { toggleWhatsapp(waId); }}
                        >
                          <X size={10} />
                        </span>
                      </span>
                    );
                  })}
                </div>
              )}

              {!allConnections && (
                <div className={classes.configDropdown} style={{ marginTop: 6 }}>
                  {whatsapps.length === 0 && (
                    <div className={classes.configEmpty}>Nenhuma conexão encontrada</div>
                  )}
                  {whatsapps.map(function (wa) {
                    var isSelected = selectedWhatsapps.indexOf(wa.id) !== -1;
                    return (
                      <div
                        key={wa.id}
                        className={classes.configDropdownItem}
                        onClick={function () { toggleWhatsapp(wa.id); }}
                      >
                        <div
                          className={
                            classes.configDropdownCheck +
                            (isSelected ? " " + classes.configDropdownCheckActive : "")
                          }
                        >
                          {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                        </div>
                        {wa.name}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </React.Fragment>
        )}

        {cfg.type === "order_status" && (
          <OrderStatusPanel
            // Um painel por node: trocar de "Status da OS" recomeça (texto digitado, leitura dos status).
            key={selectedNode.id}
            classes={classes}
            nodeData={nodeData}
            updateNodeData={updateNodeData}
            whatsapps={whatsapps}
            queues={queues}
            users={props.users}
            orderSources={props.orderSources}
            reloadOrderSources={props.reloadOrderSources}
            W={W}
          />
        )}

        {cfg.type === "bulk_message" && (
          <React.Fragment>
            {/* Aviso de banimento */}
            <div className={classes.configField}>
              <div style={{
                padding: "10px 12px",
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.25)",
                borderRadius: 8,
                fontSize: 11,
                lineHeight: 1.5,
                color: W(0.8),
              }}>
                <strong style={{ color: "#ef4444" }}>⚠ Aviso importante</strong>
                <br />
                O uso de disparo em massa pode resultar no <strong style={{ color: "#ef4444" }}>banimento permanente</strong> do número pela Meta/WhatsApp. Não nos responsabilizamos pelo uso desta funcionalidade. Use com moderação e siga as políticas do WhatsApp Business.
              </div>
            </div>

            {/* Conexão API Oficial */}
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Conexão API Oficial</span>
                <Tooltip
                  title="Selecione a conexão da API oficial da Meta para enviar as mensagens em massa. Apenas conexões da API oficial são permitidas."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>

              <div className={classes.configDropdown} style={{ marginTop: 6 }}>
                {(function () {
                  var officialConns = whatsapps.filter(function (wa) { return wa.provider === "meta-api"; });
                  if (officialConns.length === 0) {
                    return (
                      <div className={classes.configEmpty}>
                        Nenhuma conexão API oficial encontrada
                      </div>
                    );
                  }
                  return officialConns.map(function (wa) {
                    var isSelected = nodeData.whatsappId === wa.id;
                    return (
                      <div
                        key={wa.id}
                        className={classes.configDropdownItem}
                        onClick={function () {
                          updateNodeData("whatsappId", isSelected ? null : wa.id);
                        }}
                      >
                        <div
                          className={
                            classes.configDropdownCheck +
                            (isSelected ? " " + classes.configDropdownCheckActive : "")
                          }
                        >
                          {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                        </div>
                        {wa.name}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Seletor de fonte de contatos (CSV / Filtro / Lista salva = Bases) */}
            {(function () {
              var source = nodeData.source || "csv";
              var options = [
                { value: "csv", label: "CSV" },
                { value: "filter", label: "Filtro" },
                { value: "bases", label: "Lista salva" },
              ];
              return (
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Fonte de contatos</span>
                    <Tooltip
                      title="CSV: importar planilha. Filtro: selecionar contatos cadastrados na plataforma por critérios (nome, email, data, etc.)."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    {options.map(function (opt) {
                      var active = source === opt.value || (opt.value === "bases" && source === "list");
                      return (
                        <div
                          key={opt.value}
                          onClick={function () {
                            updateNodeData("source", opt.value);
                          }}
                          style={{
                            flex: 1,
                            padding: "7px 10px",
                            borderRadius: 8,
                            cursor: "pointer",
                            textAlign: "center",
                            fontSize: 11,
                            fontWeight: 600,
                            background: active ? "rgba(99,102,241,0.18)" : W(0.03),
                            border: active
                              ? "1px solid rgba(99,102,241,0.5)"
                              : "1px solid " + W(0.08),
                            color: active ? "#a5b4fc" : W(0.6),
                            transition: "all 0.15s",
                          }}
                        >
                          {opt.label}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Modo FILTRO — seleção dinâmica de contatos da plataforma */}
            {(nodeData.source === "filter") && (function () {
              // Descobre templateName conectado para oferecer dedup
              var edge = (props.edges || []).find(function (e) { return e.source === selectedNode.id; });
              var tplNode = edge
                ? (props.nodes || []).find(function (n) { return n.id === edge.target && n.data && n.data.nodeType === "send_template"; })
                : null;
              var tplName = tplNode && tplNode.data ? tplNode.data.templateName : null;

              return (
                <div className={classes.configField}>
                  <BulkFilterPanel
                    filter={nodeData.filter}
                    onFilterChange={function (next) { updateNodeData("filter", next); }}
                    templateName={tplName}
                    classes={classes}
                  />
                </div>
              );
            })()}

            {/* Modo LISTA SALVA (Bases) — selecionar e ordenar bases de contatos */}
            {(nodeData.source === "bases" || nodeData.source === "list") && (
              <React.Fragment>
                <div className={classes.configField}>
                  <BulkBasesPanel
                    baseIds={nodeData.baseIds}
                    onChange={function (ids) {
                      if (nodeData.source !== "bases") updateNodeData("source", "bases");
                      updateNodeData("baseIds", ids);
                    }}
                  />
                </div>

                {/* Configurações avançadas — agenda + deduplicação */}
                {(function () {
                  var WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
                  var wd = Array.isArray(bulkSchedule.weekdays) ? bulkSchedule.weekdays : [];
                  var toggleWeekday = function (i) {
                    var cur = Array.isArray(bulkSchedule.weekdays) ? bulkSchedule.weekdays.slice() : [];
                    var idx = cur.indexOf(i);
                    if (idx === -1) cur.push(i); else cur.splice(idx, 1);
                    cur.sort(function (a, b) { return a - b; });
                    updateBulkSchedule({ weekdays: cur });
                  };
                  var numInput = function (val, placeholder, onChange, min) {
                    return (
                      <input
                        className={classes.configInput}
                        type="number"
                        min={min != null ? min : 0}
                        placeholder={placeholder}
                        value={val != null && val !== "" ? val : ""}
                        onChange={function (e) {
                          var v = e.target.value;
                          if (v === "") { onChange(undefined); return; }
                          var n = parseInt(v, 10);
                          if (!isNaN(n)) onChange(n);
                        }}
                        style={{ width: "100%" }}
                      />
                    );
                  };
                  var infoTip = function (text) {
                    return (
                      <Tooltip title={text} placement="top" classes={{ tooltip: classes.configInfoTooltip }}>
                        <span className={classes.configInfoIcon}><Info size={13} /></span>
                      </Tooltip>
                    );
                  };
                  return (
                    <div className={classes.configField}>
                      <div
                        onClick={function () { setAdvancedOpen(!advancedOpen); }}
                        style={{
                          display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                          padding: "10px 12px", borderRadius: 8,
                          background: W(0.03), border: "1px solid " + W(0.08),
                        }}
                      >
                        <SlidersHorizontal size={14} color="#818cf8" />
                        <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: W(0.8) }}>
                          Configurações avançadas
                        </span>
                        {advancedOpen ? <ChevronUp size={15} color={W(0.5)} /> : <ChevronDown size={15} color={W(0.5)} />}
                      </div>

                      {advancedOpen && (
                        <div style={{ marginTop: 12 }}>
                          {/* Dias da semana */}
                          <div style={{ marginBottom: 14 }}>
                            <div className={classes.configLabelRow}>
                              <span className={classes.configLabel}>Dias de envio</span>
                              {infoTip("Selecione os dias em que o disparo pode acontecer. Deixe todos desmarcados para permitir todos os dias.")}
                            </div>
                            <div style={{ display: "flex", gap: 4 }}>
                              {WEEKDAYS.map(function (d, i) {
                                var on = wd.indexOf(i) !== -1;
                                return (
                                  <div
                                    key={i}
                                    onClick={function () { toggleWeekday(i); }}
                                    style={{
                                      flex: 1, textAlign: "center", padding: "6px 0", borderRadius: 6,
                                      cursor: "pointer", fontSize: 10, fontWeight: 600,
                                      background: on ? "rgba(99,102,241,0.18)" : W(0.03),
                                      border: on ? "1px solid rgba(99,102,241,0.5)" : "1px solid " + W(0.08),
                                      color: on ? "#a5b4fc" : W(0.5),
                                    }}
                                  >
                                    {d}
                                  </div>
                                );
                              })}
                            </div>
                            {wd.length === 0 && (
                              <div style={{ fontSize: 10, color: W(0.35), marginTop: 4 }}>
                                Vazio = todos os dias.
                              </div>
                            )}
                          </div>

                          {/* Janela de horário */}
                          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                            <div style={{ flex: 1 }}>
                              <div className={classes.configLabelRow}>
                                <span className={classes.configLabel}>Início</span>
                                {infoTip("Horário inicial da janela de envio (HH:mm).")}
                              </div>
                              <input
                                className={classes.configInput}
                                type="time"
                                value={bulkSchedule.windowStart || ""}
                                onChange={function (e) { updateBulkSchedule({ windowStart: e.target.value || undefined }); }}
                                style={{ width: "100%" }}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div className={classes.configLabelRow}>
                                <span className={classes.configLabel}>Fim</span>
                                {infoTip("Horário final da janela de envio (HH:mm).")}
                              </div>
                              <input
                                className={classes.configInput}
                                type="time"
                                value={bulkSchedule.windowEnd || ""}
                                onChange={function (e) { updateBulkSchedule({ windowEnd: e.target.value || undefined }); }}
                                style={{ width: "100%" }}
                              />
                            </div>
                          </div>

                          {/* Fuso horário fixo em America/Sao_Paulo (aplicado no backend). */}

                          {/* Limite diário + intervalo */}
                          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                            <div style={{ flex: 1 }}>
                              <div className={classes.configLabelRow}>
                                <span className={classes.configLabel}>Máx. por dia</span>
                                {infoTip("Máximo de envios por dia. Se vazio, o backend aplica o fallback 100.")}
                              </div>
                              {numInput(bulkSchedule.maxSendsPerDay, "100", function (v) { updateBulkSchedule({ maxSendsPerDay: v }); }, 1)}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div className={classes.configLabelRow}>
                                <span className={classes.configLabel}>Intervalo (ms)</span>
                                {infoTip("Tempo em milissegundos entre cada envio. Recomendado: 1000ms ou mais.")}
                              </div>
                              {numInput(bulkSchedule.sendIntervalMs, "1000", function (v) { updateBulkSchedule({ sendIntervalMs: v }); }, 0)}
                            </div>
                          </div>

                          {/* Período (datas) */}
                          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                            <div style={{ flex: 1 }}>
                              <div className={classes.configLabelRow}>
                                <span className={classes.configLabel}>Início (data)</span>
                                {infoTip("Data a partir da qual o disparo pode começar (opcional).")}
                              </div>
                              <CustomDatePicker
                                value={bulkSchedule.startDate || ""}
                                onChange={function (v) { updateBulkSchedule({ startDate: v || undefined }); }}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div className={classes.configLabelRow}>
                                <span className={classes.configLabel}>Fim (data)</span>
                                {infoTip("Data limite para o disparo (opcional).")}
                              </div>
                              <CustomDatePicker
                                value={bulkSchedule.endDate || ""}
                                onChange={function (v) { updateBulkSchedule({ endDate: v || undefined }); }}
                              />
                            </div>
                          </div>

                          {/* Deduplicação */}
                          <div style={{
                            borderTop: "1px solid " + W(0.06), paddingTop: 12, marginTop: 4,
                          }}>
                            <div style={{
                              fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
                              color: W(0.55), marginBottom: 10,
                            }}>
                              Deduplicação
                            </div>

                            {/* Duplicados (na base e entre bases) são removidos SEMPRE:
                                o enroll só lê a 1ª ocorrência de cada telefone e o UNIQUE
                                por execução absorve o resto — não é opção. */}
                            <div style={{ fontSize: 11, color: W(0.45), padding: "2px 0 8px" }}>
                              Números repetidos (na base ou entre bases) recebem apenas 1 mensagem.
                            </div>

                            <div style={{ marginTop: 8, marginBottom: 8 }}>
                              <div className={classes.configLabelRow}>
                                <span className={classes.configLabel}>Não reenviar a quem recebeu nos últimos (dias)</span>
                                {infoTip("Pula contatos que já receberam uma campanha nos últimos N dias. Use 0 para desativar.")}
                              </div>
                              {numInput(bulkDedup.skipReceivedCampaignDays, "0", function (v) { updateBulkDedup({ skipReceivedCampaignDays: v }); }, 0)}
                            </div>

                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0" }}>
                              <span style={{ fontSize: 11, color: W(0.7), flex: 1, paddingRight: 8 }}>
                                Respeitar opt-out (descadastro)
                                {infoTip("Não envia para contatos que solicitaram sair da lista.")}
                              </span>
                              <input
                                type="checkbox"
                                checked={bulkDedup.respectOptOut !== false}
                                onChange={function (e) { updateBulkDedup({ respectOptOut: e.target.checked }); }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </React.Fragment>
            )}

            {/* Modo CSV — importar planilha (comportamento legado) */}
            {(!nodeData.source || nodeData.source === "csv") && (
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Contatos</span>
                <Tooltip
                  title="Importe uma planilha CSV com os contatos para o disparo. O arquivo deve conter as colunas: nome, número."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>

              {/* Botões importar + exportar modelo */}
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <label style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  padding: "8px 0", borderRadius: 8, cursor: "pointer",
                  background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.2)",
                  fontSize: 11, fontWeight: 600, color: "#818cf8", transition: "all 0.15s",
                }}>
                  <Upload size={13} />
                  Importar CSV
                  <input
                    type="file"
                    accept=".csv,.txt"
                    style={{ display: "none" }}
                    onChange={function (e) {
                      var file = e.target.files && e.target.files[0];
                      if (!file) return;
                      var reader = new FileReader();
                      reader.onload = function (ev) {
                        var text = decodeCsvText(ev.target.result);
                        var lines = text.split(/\r?\n/).filter(function (l) { return l.trim(); });
                        if (lines.length === 0) return;
                        // Detectar se primeira linha e header
                        var startIdx = 0;
                        var firstLine = lines[0].toLowerCase().replace(/\s/g, "");
                        if (firstLine.indexOf("nome") !== -1 && firstLine.indexOf("numero") !== -1) {
                          startIdx = 1;
                        }
                        var contacts = [];
                        for (var i = startIdx; i < lines.length; i++) {
                          var parts = lines[i].split(/[,;]/);
                          if (parts.length < 2) continue;
                          var name = parts[0].trim();
                          var number = parts[1].trim().replace(/\D/g, "");
                          if (name && number) {
                            contacts.push({ name: name, number: number });
                          }
                        }
                        updateNodeData("contacts", contacts);
                      };
                      // CSV do Excel vem em Windows-1252, o do Bloco de Notas em UTF-16: ver decodeCsvText.
                      reader.readAsArrayBuffer(file);
                      e.target.value = "";
                    }}
                  />
                </label>
                <div
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    padding: "8px 12px", borderRadius: 8, cursor: "pointer",
                    background: W(0.04), border: "1px solid " + W(0.08),
                    fontSize: 11, fontWeight: 600, color: W(0.5), transition: "all 0.15s",
                  }}
                  onClick={function () {
                    var csv = "nome,numero\nteste,5511999999999\n";
                    var blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                    var url = URL.createObjectURL(blob);
                    var a = document.createElement("a");
                    a.href = url;
                    a.download = "modelo_contatos.csv";
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  Exportar modelo
                </div>
              </div>

              {/* Formato esperado */}
              <div style={{
                marginTop: 8, padding: "8px 10px", borderRadius: 6,
                background: W(0.03), border: "1px solid " + W(0.06),
                fontFamily: "monospace", fontSize: 10, lineHeight: 1.6,
                color: W(0.35),
              }}>
                <div style={{ color: W(0.5), fontWeight: 600, marginBottom: 2 }}>Formato do CSV:</div>
                <div>nome,numero</div>
                <div>teste,5511999999999</div>
              </div>

              {/* Preview contatos importados */}
              {(function () {
                var contacts = nodeData.contacts || [];
                if (contacts.length === 0) return null;
                return (
                  <div style={{ marginTop: 8 }}>
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      marginBottom: 6,
                    }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: W(0.7) }}>
                        {contacts.length} contato{contacts.length !== 1 ? "s" : ""} importado{contacts.length !== 1 ? "s" : ""}
                      </span>
                      <span
                        style={{
                          fontSize: 10, color: "#ef4444", cursor: "pointer",
                          fontWeight: 600, opacity: 0.8,
                        }}
                        onClick={function () { updateNodeData("contacts", []); }}
                      >
                        Limpar
                      </span>
                    </div>
                    <div style={{
                      maxHeight: 160, overflowY: "auto", borderRadius: 6,
                      border: "1px solid " + W(0.06),
                    }}>
                      <table style={{ width: "100%", fontSize: 10, borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: W(0.04) }}>
                            <th style={{ padding: "5px 8px", textAlign: "left", fontWeight: 600, color: W(0.5), borderBottom: "1px solid " + W(0.06) }}>Nome</th>
                            <th style={{ padding: "5px 8px", textAlign: "left", fontWeight: 600, color: W(0.5), borderBottom: "1px solid " + W(0.06) }}>Número</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contacts.slice(0, 50).map(function (c, i) {
                            return (
                              <tr key={i} style={{ borderBottom: "1px solid " + W(0.03) }}>
                                <td style={{ padding: "4px 8px", color: W(0.6) }}>{c.name}</td>
                                <td style={{ padding: "4px 8px", color: W(0.4), fontFamily: "monospace" }}>{c.number}</td>
                              </tr>
                            );
                          })}
                          {contacts.length > 50 && (
                            <tr>
                              <td colSpan={2} style={{ padding: "4px 8px", color: W(0.3), textAlign: "center", fontSize: 10 }}>
                                ... e mais {contacts.length - 50} contatos
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </div>
            )}

            {/* Botão disparar */}
            {(function () {
              var source = nodeData.source || "csv";
              var contacts = nodeData.contacts || [];
              var filter = nodeData.filter || {};
              var hasConnection = !!nodeData.whatsappId;
              var bulkSending = props.bulkSending || false;
              var bulkProgress = props.bulkProgress;
              var handleBulkSend = props.handleBulkSend;
              // Para filter, não temos o total exato aqui no render (vem do hook dentro do BulkFilterPanel).
              // Validação final e count real ficam no backend; aqui só exigimos conexão + algum filtro preenchido.
              var hasAnyFilter =
                !!filter.nameContains ||
                !!filter.numberContains ||
                !!filter.emailContains ||
                !!filter.createdFrom ||
                !!filter.createdTo ||
                !!filter.lastInteractionFrom ||
                !!filter.lastInteractionTo ||
                !!filter.excludeReceivedTemplateSince;
              var isBasesMode = source === "bases";
              var baseIds = nodeData.baseIds || [];
              var sourceReady;
              if (source === "csv") sourceReady = contacts.length > 0;
              else if (source === "filter") sourceReady = hasAnyFilter;
              else if (source === "bases") sourceReady = baseIds.length > 0;
              else if (source === "list") sourceReady = !!nodeData.segmentId; // legado: dispara o segmento salvo
              else sourceReady = false;

              // Descobre o node send_template conectado e valida se todas as
              // variáveis do template têm mapeamento. Bloqueia disparo se faltar.
              var tplEdge = (props.edges || []).find(function (e) { return e.source === selectedNode.id; });
              var tplNode = tplEdge
                ? (props.nodes || []).find(function (n) { return n.id === tplEdge.target && n.data && n.data.nodeType === "send_template"; })
                : null;
              var tplBody = tplNode && tplNode.data && tplNode.data.selectedTemplate
                ? tplNode.data.selectedTemplate.bodyText || ""
                : "";
              var tplVars = [];
              if (tplBody) {
                var seen = {};
                var re = /\{\{(\d+)\}\}/g;
                var m;
                while ((m = re.exec(tplBody)) !== null) {
                  if (!seen[m[1]]) { seen[m[1]] = true; tplVars.push(m[1]); }
                }
              }
              var mapping = (tplNode && tplNode.data && tplNode.data.variableMapping) || {};
              var missingVars = tplVars.filter(function (k) { return !mapping[k]; });
              var variablesOk = missingVars.length === 0;

              var canSend = hasConnection && sourceReady && variablesOk && !bulkSending;
              var basesTotal = 0;
              if (isBasesMode) {
                var basesMap = {};
                (contactBases || []).forEach(function (b) { basesMap[b.id] = b; });
                basesTotal = baseIds.reduce(function (s, id) { return s + ((basesMap[id] && basesMap[id].validRows) || 0); }, 0);
              }
              var sendLabel;
              if (!variablesOk) sendLabel = "Mapeie as variáveis (" + missingVars.length + ")";
              else if (source === "csv") sendLabel = "Disparar (" + contacts.length + " contatos)";
              else if (isBasesMode) sendLabel = basesTotal > 0 ? ("Disparar (" + basesTotal.toLocaleString("pt-BR") + " contatos)") : "Disparar";
              else sendLabel = "Disparar";

              return (
                <div className={classes.configField} style={{ marginBottom: 24 }}>
                  {canMassSend && (
                    <div
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                        padding: "12px 0", borderRadius: 10, cursor: canSend ? "pointer" : "not-allowed",
                        background: canSend ? "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)" : W(0.06),
                        color: canSend ? "#fff" : W(0.25),
                        fontWeight: 500, fontSize: 13, transition: "all 0.2s",
                        opacity: bulkSending ? 0.7 : 1,
                      }}
                      onClick={function () {
                        if (!canSend) return;
                        if (isBasesMode) openBulkConfirm();
                        else if (handleBulkSend) handleBulkSend();
                      }}
                    >
                      {bulkSending ? (
                        <CircularProgress size={14} style={{ color: "#fff" }} />
                      ) : (
                        <Send size={15} />
                      )}
                      {bulkSending ? "Enviando..." : sendLabel}
                    </div>
                  )}

                  {/* Progress bar */}
                  {bulkProgress && !bulkProgress.done && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{
                        display: "flex", justifyContent: "space-between", fontSize: 10,
                        color: W(0.5), marginBottom: 4,
                      }}>
                        <span>{bulkProgress.current || 0} / {bulkProgress.total || 0}</span>
                        <span>{bulkProgress.percent || 0}%</span>
                      </div>
                      <div style={{
                        height: 6, borderRadius: 3, background: W(0.06), overflow: "hidden",
                      }}>
                        <div style={{
                          height: "100%", borderRadius: 3, transition: "width 0.3s ease",
                          background: "linear-gradient(90deg, #4f7cff, #3b82f6)",
                          width: (bulkProgress.percent || 0) + "%",
                        }} />
                      </div>
                      <div style={{
                        display: "flex", justifyContent: "space-between", fontSize: 10,
                        color: W(0.4), marginTop: 4,
                      }}>
                        <span style={{ color: "#22c55e" }}>{bulkProgress.sent || 0} enviados</span>
                        {(bulkProgress.failed || 0) > 0 && (
                          <span style={{ color: "#ef4444" }}>{bulkProgress.failed} falhas</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Resultado final */}
                  {bulkProgress && bulkProgress.done && (
                    <div style={{
                      marginTop: 8, padding: "8px 12px", borderRadius: 8,
                      background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.15)",
                      fontSize: 11, color: W(0.6),
                    }}>
                      Concluído{bulkProgress.elapsed != null ? " em " + bulkProgress.elapsed + "s" : ""} — {bulkProgress.sent} enviados
                      {(bulkProgress.failed || 0) > 0 ? ", " + bulkProgress.failed + " falhas" : ""}
                    </div>
                  )}

                  {/* Validação */}
                  {!hasConnection && (
                    <div style={{ fontSize: 10, color: "#f59e0b", marginTop: 6 }}>
                      Selecione uma conexão API oficial
                    </div>
                  )}

                  {/* Monitor de execução durável (mass-send) */}
                  {!props.readOnly && massMonitor}
                </div>
              );
            })()}

            {/* Modal de confirmação pré-disparo */}
            <MassSendConfirmModal
              open={confirmOpen}
              onClose={function () { setConfirmOpen(false); }}
              onConfirm={handleConfirmDispatch}
              dispatching={dispatching}
              validating={validating}
              validation={validation}
              bases={getSelectedBases()}
              connectionName={(function () {
                var wa = (whatsapps || []).find(function (w) { return w.id === nodeData.whatsappId; });
                return wa ? wa.name : null;
              })()}
              templateName={(function () {
                var t = getConnectedTemplateNode();
                return t && t.data ? t.data.templateName : null;
              })()}
              schedule={nodeData.schedule}
            />
          </React.Fragment>
        )}

        {cfg.type === "send_template" && (function () {
          // Ligado ao "Status da OS": o template sai sozinho para cada OS, então só
          // servem os que o envio automático preenche, e as variáveis vêm do aviso.
          var tplSource = upstreamNode(props.nodes, props.edges, selectedNode.id);
          var viaOrderStatus = !!(tplSource && tplSource.data && tplSource.data.nodeType === "order_status");
          return (
          <React.Fragment>
            {/* Template selector */}
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Template</span>
                <Tooltip
                  title={viaOrderStatus
                    ? "Template aprovado pela Meta na conexão escolhida no 'Status da OS'. Ele sai sozinho para cada OS: templates com mídia no cabeçalho ou botão com variável não servem."
                    : "Selecione o template aprovado pela Meta para enviar no disparo. Conecte este node ao node de Disparo em Massa para carregar os templates da conexao."}
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>

              {(function () {
                var templates = props.bulkTemplates || [];
                var loadingTemplates = props.loadingTemplates || false;
                var selectedTemplateId = nodeData.templateName || null;

                if (loadingTemplates) {
                  return (
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      padding: "16px 0", color: W(0.4), fontSize: 11,
                    }}>
                      <CircularProgress size={14} style={{ color: "#4f7cff", marginRight: 8 }} />
                      Carregando templates...
                    </div>
                  );
                }

                if (templates.length === 0) {
                  return (
                    <div style={{
                      padding: "10px 12px", borderRadius: 8,
                      background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.12)",
                      fontSize: 11, color: isDark ? W(0.5) : W(0.85), marginTop: 6,
                    }}>
                      {viaOrderStatus
                        ? "Nenhum template encontrado. Escolha a conexão da API oficial no node \"Status da OS\"."
                        : "Nenhum template encontrado. Conecte este node a um node \"Disparo em massa\" com uma conexao API oficial configurada."}
                    </div>
                  );
                }

                return (
                  <div className={classes.configDropdown} style={{ marginTop: 6, maxHeight: 200, overflowY: "auto" }}>
                    {templates.map(function (tpl) {
                      var isSelected = selectedTemplateId === tpl.name;
                      var problem = viaOrderStatus ? orderTemplateProblem(tpl) : null;
                      return (
                        <div
                          key={tpl.name + "_" + tpl.language}
                          className={classes.configDropdownItem}
                          style={{
                            flexDirection: "column", alignItems: "flex-start", padding: "8px 10px",
                            opacity: problem && !isSelected ? 0.55 : 1,
                            cursor: problem && !isSelected ? "not-allowed" : "pointer",
                          }}
                          aria-disabled={problem && !isSelected ? true : undefined}
                          title={problem ? "Não serve para o envio automático: " + problem : undefined}
                          onClick={function () {
                            if (isSelected) {
                              updateNodeData("templateName", null);
                              updateNodeData("selectedTemplate", null);
                              if (viaOrderStatus) updateNodeData("templateVariables", {});
                            } else if (problem) {
                              return;
                            } else {
                              if (viaOrderStatus) updateNodeData("templateVariables", initialOrderVariables(tpl));
                              updateNodeData("templateName", tpl.name);
                              var bodyComp = (tpl.components || []).find(function (c) { return c.type === "BODY"; });
                              var btnComp = (tpl.components || []).find(function (c) { return c.type === "BUTTONS"; });
                              var headerComp = (tpl.components || []).find(function (c) { return c.type === "HEADER"; });
                              var buttons = btnComp ? (btnComp.buttons || []).map(function (b) {
                                return { text: b.text, type: b.type };
                              }) : [];
                              updateNodeData("selectedTemplate", {
                                name: tpl.name,
                                language: tpl.language,
                                bodyText: bodyComp ? bodyComp.text : "",
                                buttons: buttons,
                                header: headerComp ? {
                                  format: headerComp.format,
                                  text: headerComp.text || null,
                                } : null,
                              });
                            }
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 6, width: "100%" }}>
                            <div
                              className={
                                classes.configDropdownCheck +
                                (isSelected ? " " + classes.configDropdownCheckActive : "")
                              }
                            >
                              {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 11 }}>{tpl.name}</div>
                              <div style={{ fontSize: 9, opacity: 0.4 }}>{tpl.language} • {tpl.status}</div>
                              {problem && (
                                <div style={{ fontSize: 10, marginTop: 2, color: W(0.6) }}>Não serve: {problem}</div>
                              )}
                            </div>
                          </div>
                          {isSelected && (function () {
                            var headerComp = (tpl.components || []).find(function (c) { return c.type === "HEADER"; });
                            if (!headerComp) return null;
                            var fmt = String(headerComp.format || "").toUpperCase();
                            var label = fmt === "TEXT" ? "Cabeçalho: Texto"
                              : fmt === "IMAGE" ? "Cabeçalho: Imagem"
                              : fmt === "VIDEO" ? "Cabeçalho: Vídeo"
                              : fmt === "DOCUMENT" ? "Cabeçalho: Documento"
                              : null;
                            if (!label) return null;
                            return (
                              <div style={{
                                marginTop: 6, padding: "4px 8px", borderRadius: 6,
                                background: "rgba(99,102,241,0.08)",
                                border: "1px solid rgba(99,102,241,0.18)",
                                fontSize: 10, lineHeight: 1.4,
                                color: W(0.65),
                                width: "100%",
                              }}>
                                {label}
                                {fmt === "TEXT" && headerComp.text ? (
                                  <span style={{ opacity: 0.55 }}> — {headerComp.text}</span>
                                ) : null}
                              </div>
                            );
                          })()}
                          {isSelected && (function () {
                            var bodyComp = (tpl.components || []).find(function (c) { return c.type === "BODY"; });
                            if (!bodyComp) return null;
                            return (
                              <div style={{
                                marginTop: 6, padding: "6px 8px", borderRadius: 6,
                                background: W(0.03), fontSize: 10,
                                lineHeight: 1.5, color: W(0.5),
                                whiteSpace: "pre-wrap", width: "100%",
                              }}>
                                {bodyComp.text}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Variáveis do template no envio por status de OS (e o aviso de template que não serve mais) */}
            {nodeData.templateName && viaOrderStatus && (
              <div className={classes.configField}>
                <OrderTemplateVariables
                  nodeData={nodeData}
                  templates={props.bulkTemplates || []}
                  loading={!!props.loadingTemplates}
                  updateNodeData={updateNodeData}
                  isDark={isDark}
                />
              </div>
            )}

            {/* Mapeamento de variáveis do template selecionado */}
            {nodeData.selectedTemplate && !viaOrderStatus && (function () {
              // Node bulk_message conectado a montante (fonte deste send_template)
              var inEdge = (props.edges || []).find(function (e) { return e.target === selectedNode.id; });
              var srcNode = inEdge ? (props.nodes || []).find(function (n) { return n.id === inEdge.source; }) : null;
              var bulkNode = srcNode && srcNode.data && srcNode.data.nodeType === "bulk_message" ? srcNode : null;
              var bulkData = (bulkNode && bulkNode.data) || {};
              var bIds = bulkData.baseIds || [];

              // Une os cabeçalhos (columnMapping) das bases selecionadas
              var headerSet = {};
              var headers = [];
              (contactBases || []).forEach(function (b) {
                if (bIds.indexOf(b.id) === -1) return;
                var cm = b.columnMapping || {};
                var cols = [];
                if (cm.phoneColumn) cols.push(cm.phoneColumn);
                if (cm.nameColumn) cols.push(cm.nameColumn);
                (cm.variableColumns || []).forEach(function (c) { cols.push(c); });
                cols.forEach(function (c) {
                  if (c && !headerSet[c]) { headerSet[c] = true; headers.push(c); }
                });
              });

              // Espelha o payload do disparo real (WorkflowController lê os
              // mesmos campos do node): cada fonte manda o que o validate
              // precisa pra estimar — csv → contacts, filter → filter,
              // list legado → segmentId.
              var previewContext = {
                whatsappId: bulkData.whatsappId,
                templateName: nodeData.templateName,
                language: nodeData.selectedTemplate.language,
                bodyText: nodeData.selectedTemplate.bodyText,
                source: (bulkData.source === "list" && !bulkData.segmentId)
                  ? "bases"
                  : (bulkData.source || "csv"),
                baseIds: bIds,
                contacts: bulkData.contacts,
                filter: bulkData.filter,
                segmentId: bulkData.segmentId,
                schedule: bulkData.schedule,
              };

              return (
                <div className={classes.configField}>
                  <TemplateVariableMapper
                    selectedTemplate={nodeData.selectedTemplate}
                    mapping={nodeData.variableMapping}
                    onChange={function (next) { updateNodeData("variableMapping", next); }}
                    baseColumns={headers}
                    previewContext={previewContext}
                  />
                </div>
              );
            })()}

            {/* Metricas */}
            {(function () {
              var stats = nodeData.stats || {};
              var sent = stats.sent || 0;
              if (sent === 0) return null;
              var delivered = stats.delivered || 0;
              var read = stats.read || 0;
              return (
                <div className={classes.configField}>
                  <span className={classes.configLabel}>Metricas</span>
                  <div style={{
                    display: "flex", gap: 8, marginTop: 6,
                  }}>
                    <div style={{
                      flex: 1, textAlign: "center", padding: "8px 0", borderRadius: 8,
                      background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.12)",
                    }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#22c55e" }}>{sent}</div>
                      <div style={{ fontSize: 9, color: W(0.4) }}>Enviados</div>
                    </div>
                    <div style={{
                      flex: 1, textAlign: "center", padding: "8px 0", borderRadius: 8,
                      background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.12)",
                    }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#22c55e" }}>{sent > 0 ? ((delivered / sent) * 100).toFixed(1) : "0.0"}%</div>
                      <div style={{ fontSize: 9, color: W(0.4) }}>Entregue</div>
                    </div>
                    <div style={{
                      flex: 1, textAlign: "center", padding: "8px 0", borderRadius: 8,
                      background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.12)",
                    }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#22c55e" }}>{sent > 0 ? ((read / sent) * 100).toFixed(1) : "0.0"}%</div>
                      <div style={{ fontSize: 9, color: W(0.4) }}>Lido</div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Info */}
            {viaOrderStatus ? (
              <div style={{ fontSize: 11, lineHeight: 1.55, color: W(0.6), marginTop: 4 }}>
                Este template sai pela conexão do "Status da OS", uma vez para cada OS que mudar para um dos status
                escolhidos.
              </div>
            ) : (
              <div style={{
                padding: "10px 12px", borderRadius: 8,
                background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.12)",
                fontSize: 11, lineHeight: 1.5, color: isDark ? W(0.5) : W(0.85), marginTop: 4,
              }}>
                Conecte ao node "Disparo em massa" para definir a conexao e os contatos. O template selecionado sera enviado para todos os contatos importados.
              </div>
            )}
          </React.Fragment>
          );
        })()}

        {cfg.type === "ai_triage" && (
          <React.Fragment>
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Triagem</span>
                <Tooltip
                  title="A triagem que a IA conduz nas conversas que casarem com o 'Receber mensagem'. Só aparecem as triagens publicadas em 'Gerenciar Triagens'."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>
              <div className={classes.configDropdown} style={{ marginTop: 6 }}>
                {triages === null && (
                  <div className={classes.configEmpty}>
                    <CircularProgress size={12} style={{ marginRight: 8 }} />
                    Carregando triagens...
                  </div>
                )}
                {triages && triages.length === 0 && (
                  <div className={classes.configEmpty}>
                    Nenhuma triagem publicada. Crie e publique uma em "Gerenciar Triagens".
                  </div>
                )}
                {(triages || []).map(function (t) {
                  var isSelected = Number(nodeData.definitionId) === t.id;
                  return (
                    <div
                      key={t.id}
                      className={classes.configDropdownItem}
                      onClick={function () {
                        updateNodeData("definitionId", isSelected ? null : t.id);
                        updateNodeData("definitionName", isSelected ? null : t.name);
                      }}
                    >
                      <div
                        className={
                          classes.configDropdownCheck +
                          (isSelected ? " " + classes.configDropdownCheckActive : "")
                        }
                      >
                        {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                      </div>
                      {t.name}
                    </div>
                  );
                })}
              </div>
              {nodeData.definitionId && triages && !triages.some(function (t) { return t.id === Number(nodeData.definitionId); }) && (
                <div style={{ fontSize: 11, marginTop: 6, color: isDark ? "#fbbf24" : "#b45309" }}>
                  A triagem escolhida ({nodeData.definitionName || "#" + nodeData.definitionId}) não está mais publicada.
                </div>
              )}
            </div>
            <div className={classes.configField}>
              <div style={{ fontSize: 11, lineHeight: 1.55, color: W(0.6) }}>
                Ligue este node ao "Receber mensagem". A conversa que casar com o gatilho começa nesta triagem, e a
                primeira mensagem do cliente já é aproveitada. A triagem não começa em conversa com uma pessoa, nem
                nas 24 h depois de uma triagem terminada do mesmo contato. Não ligue o mesmo gatilho ao "Agente IA".
              </div>
            </div>
          </React.Fragment>
        )}

        {cfg.type === "ai_agent" && (
          <React.Fragment>
            <AgentPicker
              // Um campo por bloco: trocar de bloco fecha a confirmação de transformar.
              key={selectedNode.id}
              classes={classes}
              W={W}
              nodeData={nodeData}
              mode={agentMode}
              agents={props.agentOptions}
              hasKnowledgeTools={hasKnowledgeTools}
              // Só ver o fluxo: o painel fica inerte, e o agente abre pelo topo (agentLive).
              canViewAgents={canViewAgents && !props.readOnly}
              canManageAgents={canManageAgents && !props.readOnly}
              dirty={props.dirty}
              onChoose={function (agentId) { updateNodeData("agentId", agentId); }}
              onOpenManager={function (agentId) { props.openAgentManager({ nodeId: selectedNode.id, agentId: agentId }); }}
              onConvert={function () { return props.convertNodeToAgent(selectedNode.id); }}
            />

            {/* Botão de guia das automações */}
            <div className={classes.configField}>
              <button
                onClick={function () { setGuideOpen(true); }}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  background: "rgba(91,124,255,0.10)",
                  border: "1px solid rgba(91,124,255,0.25)",
                  color: W(0.88),
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  transition: "background 0.15s, border-color 0.15s",
                }}
                onMouseEnter={function (e) {
                  e.currentTarget.style.background = "rgba(91,124,255,0.16)";
                  e.currentTarget.style.borderColor = "rgba(91,124,255,0.40)";
                }}
                onMouseLeave={function (e) {
                  e.currentTarget.style.background = "rgba(91,124,255,0.10)";
                  e.currentTarget.style.borderColor = "rgba(91,124,255,0.25)";
                }}
              >
                <BookOpen size={14} />
                Guia das automações da IA
              </button>
            </div>

            {/* O que é do agente: com um agente escolhido, vem dele (bloco 3b). */}
            {agentMode === "custom" && (
              <React.Fragment>
                {/* Usuário IA representante */}
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Usuário IA representante</span>
                    <Tooltip
                      title="Este usuário aparecerá como atendente nos tickets enquanto a IA estiver respondendo. Permite que a equipe veja em /tickets quem está atendendo cada conversa. Apenas usuários marcados como Usuário IA aparecem aqui."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  {aiUsersLoading && (
                    <div style={{
                      padding: "12px 14px",
                      borderRadius: 8,
                      border: "1px solid " + W(0.08),
                      background: W(0.03),
                      fontSize: 12,
                      color: W(0.55),
                      display: "flex",
                      alignItems: "center",
                      gap: 8
                    }}>
                      <CircularProgress size={12} style={{ color: W(0.55) }} />
                      Carregando usuários IA…
                    </div>
                  )}
                  {!aiUsersLoading && aiUsers.length === 0 && (
                    <div style={{
                      padding: "12px 14px",
                      borderRadius: 8,
                      border: "1px dashed " + W(0.12),
                      background: W(0.02),
                      fontSize: 11,
                      color: W(0.5),
                      textAlign: "center",
                      lineHeight: 1.5
                    }}>
                      Nenhum Usuário IA cadastrado.<br />
                      Crie um usuário marcado como "Usuário IA" em <strong>Usuários</strong> para listar aqui.
                    </div>
                  )}
                  {!aiUsersLoading && aiUsers.length > 0 && (
                    <div style={{
                      maxHeight: 180, overflowY: "auto", marginTop: 4,
                      borderRadius: 8, border: "1px solid " + W(0.08)
                    }}>
                      {/* Opção "Nenhum" */}
                      {(function () {
                        var isSelected = !nodeData.aiUserId;
                        return (
                          <div
                            style={{
                              display: "flex", alignItems: "center", gap: 8,
                              padding: "8px 12px", cursor: "pointer",
                              background: isSelected ? "rgba(91,124,255,0.12)" : "transparent",
                              borderBottom: "1px solid " + W(0.04),
                              transition: "background 0.15s"
                            }}
                            onClick={function () { updateNodeData("aiUserId", null); }}
                          >
                            <div style={{
                              width: 18, height: 18, borderRadius: "50%",
                              border: isSelected ? "5px solid #5B7CFF" : "2px solid " + W(0.15),
                              background: "transparent",
                              transition: "all 0.15s", flexShrink: 0
                            }} />
                            <span style={{ fontSize: 12, color: isSelected ? "#fff" : W(0.55), fontStyle: "italic" }}>
                              Nenhum (sem atribuição)
                            </span>
                          </div>
                        );
                      })()}
                      {aiUsers.map(function (u) {
                        var isSelected = nodeData.aiUserId === u.id || nodeData.aiUserId === String(u.id);
                        var initials = (u.name || "?")
                          .split(" ")
                          .map(function (p) { return p.charAt(0); })
                          .join("")
                          .slice(0, 2)
                          .toUpperCase();
                        return (
                          <div
                            key={u.id}
                            style={{
                              display: "flex", alignItems: "center", gap: 10,
                              padding: "8px 12px", cursor: "pointer",
                              background: isSelected ? "rgba(91,124,255,0.12)" : "transparent",
                              borderBottom: "1px solid " + W(0.04),
                              transition: "background 0.15s"
                            }}
                            onClick={function () { updateNodeData("aiUserId", u.id); }}
                          >
                            <div style={{
                              width: 18, height: 18, borderRadius: "50%",
                              border: isSelected ? "5px solid #5B7CFF" : "2px solid " + W(0.15),
                              background: "transparent",
                              transition: "all 0.15s", flexShrink: 0
                            }} />
                            <div style={{
                              width: 26, height: 26, borderRadius: "50%",
                              background: "linear-gradient(135deg, #8b5cf6 0%, #5B7CFF 100%)",
                              color: "#fff", fontSize: 10, fontWeight: 700,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              flexShrink: 0,
                              backgroundImage: u.profileImage ? "url(" + u.profileImage + ")" : undefined,
                              backgroundSize: "cover",
                              backgroundPosition: "center"
                            }}>
                              {!u.profileImage && initials}
                            </div>
                            <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: isSelected ? "#fff" : W(0.78), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {u.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Prompt */}
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Prompt</span>
                    <Tooltip
                      title="Instrucoes que definem o comportamento da IA. Descreva a personalidade, regras, tom de voz e como ela deve responder ao cliente."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  <div className={classes.configTextareaWrap}>
                    <Tooltip title="Expandir / Editar" placement="top">
                      <div
                        className={classes.configExpandBtn}
                        onClick={function () { setPromptModalOpen(true); }}
                      >
                        <Maximize2 size={12} />
                      </div>
                    </Tooltip>
                    {/* Prompt com highlight de tags */}
                    {(function () {
                      var promptVal = nodeData.prompt || "";
                      var tagColorMap = {};
                      var agentIdForTags = selectedNode.id;
                      var toolIdsForTags = edges
                        .filter(function (e) { return e.source === agentIdForTags && e.sourceHandle === "tools"; })
                        .map(function (e) { return e.target; });
                      nodes.forEach(function (n) {
                        if (toolIdsForTags.indexOf(n.id) === -1) return;
                        if (n.data.nodeType === "tool_files") {
                          (n.data.files || []).forEach(function (f) {
                            tagColorMap["{{" + f.name + "}}"] = "#f59e0b";
                            tagColorMap["{{" + (f.originalName || f.name) + "}}"] = "#f59e0b";
                          });
                        }
                        if (n.data.nodeType === "tool_memory") {
                          (n.data.memories || []).forEach(function (m) {
                            tagColorMap["{{memoria_" + m.name.replace(/\s+/g, "_") + "}}"] = "#ec4899";
                          });
                        }
                        if (n.data.nodeType === "tool_crm_create") {
                          tagColorMap["{{crm_criar_card}}"] = "#10b981";
                          (n.data.fields || []).forEach(function (f) {
                            tagColorMap["{{crm_campo_" + f.name.replace(/\s+/g, "_") + "}}"] = "#10b981";
                          });
                        }
                        if (n.data.nodeType === "tool_crm_update") {
                          tagColorMap["{{crm_atualizar_card}}"] = "#06b6d4";
                        }
                        if (n.data.nodeType === "tool_crm_status") {
                          tagColorMap["{{crm_mover_card}}"] = "#f97316";
                        }
                      });

                      var hasTags = /\{\{[^}]+\}\}/.test(promptVal);

                      if (!hasTags) {
                        return (
                          <textarea
                            className={classes.configTextarea}
                            value={promptVal}
                            onChange={function (e) { updateNodeData("prompt", e.target.value); }}
                            placeholder={"Voce e um assistente virtual da empresa X.\nSeja educado, objetivo e responda apenas sobre nossos produtos e servicos."}
                            style={{ paddingRight: 40 }}
                          />
                        );
                      }

                      // Renderizar preview com tags coloridas
                      var parts = [];
                      var regex = /\{\{[^}]+\}\}/g;
                      var lastIdx = 0;
                      var match;
                      while ((match = regex.exec(promptVal)) !== null) {
                        if (match.index > lastIdx) {
                          parts.push({ text: promptVal.substring(lastIdx, match.index), isTag: false });
                        }
                        parts.push({ text: match[0], isTag: true });
                        lastIdx = match.index + match[0].length;
                      }
                      if (lastIdx < promptVal.length) {
                        parts.push({ text: promptVal.substring(lastIdx), isTag: false });
                      }

                      return (
                        <div
                          onClick={function () { setPromptModalOpen(true); }}
                          style={{
                            padding: "10px 40px 10px 12px",
                            fontSize: 12, lineHeight: 1.6,
                            color: W(0.75),
                            whiteSpace: "pre-wrap", wordWrap: "break-word",
                            maxHeight: 200, overflowY: "auto",
                            cursor: "text",
                            background: W(0.04),
                            borderRadius: 8,
                            border: "1px solid " + W(0.08),
                            transition: "border-color 0.2s"
                          }}
                        >
                          {parts.map(function (part, i) {
                            if (!part.isTag) return React.createElement("span", { key: i }, part.text);
                            var tagColor = tagColorMap[part.text] || "#5B7CFF";
                            return React.createElement("span", {
                              key: i,
                              style: {
                                display: "inline",
                                background: tagColor + "20",
                                border: "1px solid " + tagColor + "45",
                                borderRadius: 4, padding: "2px 6px",
                                color: tagColor, fontWeight: 600,
                                fontSize: 11, lineHeight: 2
                              }
                            }, part.text);
                          })}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Recursos conectados (badges estilo n8n) */}
                  {(function () {
                    var agentId = selectedNode.id;
                    var toolIds = edges
                      .filter(function (e) { return e.source === agentId && e.sourceHandle === "tools"; })
                      .map(function (e) { return e.target; });
                    if (toolIds.length === 0) return null;

                    var badges = [];
                    nodes.forEach(function (n) {
                      if (toolIds.indexOf(n.id) === -1) return;
                      if (n.data.nodeType === "tool_files") {
                        var files = n.data.files || [];
                        files.forEach(function (f) {
                          badges.push({ label: f.name || f.originalName, icon: "file", color: "#f59e0b" });
                        });
                      }
                      if (n.data.nodeType === "tool_memory") {
                        var mems = n.data.memories || [];
                        mems.forEach(function (m) {
                          badges.push({ label: m.name, icon: "brain", color: "#ec4899" });
                        });
                      }
                      if (n.data.nodeType === "tool_websearch") {
                        badges.push({ label: "Web Search", icon: "globe", color: "#06b6d4" });
                      }
                      if (n.data.nodeType === "tool_crm_create") {
                        badges.push({ label: "Criar Card", icon: "kanban", color: "#10b981" });
                        var fields = n.data.fields || [];
                        fields.forEach(function (f) {
                          badges.push({ label: f.name, icon: "kanban", color: "#10b981" });
                        });
                      }
                      if (n.data.nodeType === "tool_crm_update") {
                        badges.push({ label: "Atualizar Card", icon: "kanban", color: "#06b6d4" });
                      }
                      if (n.data.nodeType === "tool_crm_status") {
                        badges.push({ label: "Status Card", icon: "kanban", color: "#f97316" });
                      }
                    });
                    if (badges.length === 0) return null;

                    var iconMap = {
                      file: FileUp,
                      brain: Brain,
                      globe: Globe,
                      kanban: SquareKanban,
                    };

                    return (
                      <div style={{
                        display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8,
                        padding: "8px 10px", borderRadius: 8,
                        background: W(0.03),
                        border: "1px solid " + W(0.06)
                      }}>
                        <div style={{
                          width: "100%", fontSize: 10, color: W(0.35),
                          marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5
                        }}>
                          Recursos conectados
                        </div>
                        {badges.map(function (b, i) {
                          var BadgeIcon = iconMap[b.icon] || FileUp;
                          return (
                            <div key={i} style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              padding: "3px 8px 3px 6px", borderRadius: 6,
                              background: b.color + "18",
                              border: "1px solid " + b.color + "30",
                              fontSize: 11, color: b.color,
                              lineHeight: 1.3, maxWidth: "100%"
                            }}>
                              <BadgeIcon size={11} strokeWidth={2.5} style={{ flexShrink: 0 }} />
                              <span style={{
                                overflow: "hidden", textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                              }}>
                                {b.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Modelo de IA */}
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Modelo de IA</span>
                    <Tooltip
                      title="Modelo da OpenAI que gera as respostas. A familia GPT-5 (Nano/Mini/Full) suporta controle de raciocinio e costuma ser mais economica por token; GPT-4.1 e 4o nao usam raciocinio. Quanto mais capaz o modelo, maior o custo por mensagem."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  {(function () {
                    var selModelValue = nodeData.model || "gpt-5-mini";
                    var sel = AI_AGENT_MODELS.find(function (m) { return m.value === selModelValue; }) || AI_AGENT_MODELS[1];
                    return (
                      <React.Fragment>
                        <div style={{ position: "relative" }}>
                          <div
                            className={classes.configInput}
                            onClick={function () { setModelDropdownOpen(!modelDropdownOpen); }}
                            style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
                          >
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {sel.label}
                              <span style={{ opacity: 0.5 }}>{" — " + sel.short}</span>
                            </span>
                            <ChevronDown
                              size={16}
                              style={{ flexShrink: 0, opacity: 0.6, marginLeft: 8, transition: "transform .15s", transform: modelDropdownOpen ? "rotate(180deg)" : "none" }}
                            />
                          </div>
                          {modelDropdownOpen && (
                            <React.Fragment>
                              <div
                                onClick={function () { setModelDropdownOpen(false); }}
                                style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 40 }}
                              />
                              <div style={{
                                position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50,
                                background: PANEL, border: "1px solid " + W(0.12), borderRadius: 8,
                                boxShadow: "0 8px 24px rgba(0,0,0,0.5)", padding: 4, maxHeight: 300, overflowY: "auto"
                              }}>
                                {AI_AGENT_MODEL_GROUPS.map(function (group) {
                                  return (
                                    <div key={group}>
                                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: W(0.35), padding: "8px 10px 4px" }}>
                                        {group}
                                      </div>
                                      {AI_AGENT_MODELS.filter(function (m) { return m.group === group; }).map(function (m) {
                                        var active = m.value === selModelValue;
                                        return (
                                          <div
                                            key={m.value}
                                            onClick={function () { updateNodeData("model", m.value); setModelDropdownOpen(false); }}
                                            onMouseEnter={function (e) { if (!active) { e.currentTarget.style.background = W(0.06); } }}
                                            onMouseLeave={function (e) { if (!active) { e.currentTarget.style.background = "transparent"; } }}
                                            style={{
                                              padding: "7px 10px", borderRadius: 6, cursor: "pointer",
                                              background: active ? "rgba(91,124,255,0.20)" : "transparent",
                                              color: active ? "#fff" : W(0.82)
                                            }}
                                          >
                                            <span style={{ fontSize: 13, fontWeight: active ? 600 : 500 }}>{m.label}</span>
                                            <span style={{ fontSize: 12, opacity: 0.5 }}>{" — " + m.short}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                })}
                              </div>
                            </React.Fragment>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: W(0.45), marginTop: 6, lineHeight: 1.4 }}>
                          {sel.desc}
                        </div>
                      </React.Fragment>
                    );
                  })()}
                </div>

                {/* Nivel de raciocinio */}
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Nivel de raciocinio</span>
                    <Tooltip
                      title="Controla quanto o modelo 'pensa' antes de responder. Os tokens de raciocinio sao invisiveis para o cliente, mas cobrados como saida — entao menos raciocinio = mais barato. Para atendimento com respostas curtas, Baixo ou Minimo costuma bastar; aumente em fluxos que exigem logica ou muito contexto. Disponivel apenas para modelos GPT-5."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  {(nodeData.model || "gpt-5-mini").indexOf("gpt-5") === 0 ? (
                    <div className={classes.configOptionGroup}>
                      {AI_AGENT_REASONING_LEVELS.map(function (opt) {
                        return (
                          <div
                            key={opt.value}
                            className={classes.configOptionBtn + ((nodeData.reasoningEffort || "medium") === opt.value ? " " + classes.configOptionBtnActive : "")}
                            onClick={function () { updateNodeData("reasoningEffort", opt.value); }}
                          >
                            {opt.label}
                            <span className={classes.configOptionDesc}>{opt.desc}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: W(0.4), padding: "8px 10px", borderRadius: 8, background: W(0.03), border: "1px solid " + W(0.06) }}>
                      O modelo selecionado nao usa nivel de raciocinio. Escolha um modelo GPT-5 para ajustar.
                    </div>
                  )}
                </div>

                {/* Memoria */}
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Memoria</span>
                    <Tooltip
                      title="Quantidade de mensagens anteriores da conversa que a IA vai considerar para formular a resposta. Quanto maior, mais contexto a IA tera, porem maior o custo de tokens."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  <div className={classes.configSliderRow}>
                    <input
                      type="range"
                      className={classes.configSlider}
                      min={1}
                      max={100}
                      step={1}
                      value={nodeData.contextWindow || 20}
                      onChange={function (e) {
                        updateNodeData("contextWindow", parseInt(e.target.value, 10));
                      }}
                    />
                    <span className={classes.configSliderValue}>
                      {nodeData.contextWindow || 20}
                    </span>
                  </div>
                </div>

                {/* Tempo de espera */}
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Tempo de espera</span>
                    <Tooltip
                      title="Tempo em segundos que a IA aguarda apos a ultima mensagem do cliente antes de responder. Se o cliente enviar outra mensagem dentro desse tempo, a contagem reinicia. Isso permite que o cliente envie varias mensagens seguidas antes da IA processar tudo junto."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  <div className={classes.configSliderRow}>
                    <input
                      type="range"
                      className={classes.configSlider}
                      min={3}
                      max={60}
                      step={1}
                      value={nodeData.waitTime || 12}
                      onChange={function (e) {
                        updateNodeData("waitTime", parseInt(e.target.value, 10));
                      }}
                    />
                    <span className={classes.configSliderValue}>
                      {nodeData.waitTime || 12}s
                    </span>
                  </div>
                </div>

                {/* Estilo de resposta */}
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Estilo de resposta</span>
                    <Tooltip
                      title="Define o tamanho e nivel de detalhe das respostas da IA."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  <div className={classes.configOptionGroup}>
                    <div
                      className={classes.configOptionBtn + ((nodeData.responseStyle || "short") === "short" ? " " + classes.configOptionBtnActive : "")}
                      onClick={function () { updateNodeData("responseStyle", "short"); }}
                    >
                      <span className={classes.configOptionIcon}>&#9889;</span>
                      Curta
                      <span className={classes.configOptionDesc}>Resumida e direta</span>
                    </div>
                    <div
                      className={classes.configOptionBtn + ((nodeData.responseStyle) === "medium" ? " " + classes.configOptionBtnActive : "")}
                      onClick={function () { updateNodeData("responseStyle", "medium"); }}
                    >
                      <span className={classes.configOptionIcon}>&#9997;&#65039;</span>
                      Media
                      <span className={classes.configOptionDesc}>Equilibrada e clara</span>
                    </div>
                    <div
                      className={classes.configOptionBtn + ((nodeData.responseStyle) === "detailed" ? " " + classes.configOptionBtnActive : "")}
                      onClick={function () { updateNodeData("responseStyle", "detailed"); }}
                    >
                      <span className={classes.configOptionIcon}>&#128214;</span>
                      Detalhada
                      <span className={classes.configOptionDesc}>Completa e explicativa</span>
                    </div>
                  </div>
                </div>

                {/* Enviar em blocos */}
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Mensagens em blocos</span>
                    <Tooltip
                      title="Quando ativado, a IA quebra respostas longas em multiplos baloes de mensagem, simulando uma conversa mais natural. Ex: em vez de um unico texto longo, envia cada frase ou paragrafo como mensagem separada."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  <div className={classes.configOptionGroup}>
                    <div
                      className={classes.configOptionBtn + (!(nodeData.splitMessages) ? " " + classes.configOptionBtnActive : "")}
                      onClick={function () { updateNodeData("splitMessages", false); }}
                    >
                      <span className={classes.configOptionIcon}>&#128172;</span>
                      Unica
                      <span className={classes.configOptionDesc}>Uma mensagem so</span>
                    </div>
                    <div
                      className={classes.configOptionBtn + ((nodeData.splitMessages) ? " " + classes.configOptionBtnActive : "")}
                      onClick={function () { updateNodeData("splitMessages", true); }}
                    >
                      <span className={classes.configOptionIcon}>&#128172;&#128172;</span>
                      Em blocos
                      <span className={classes.configOptionDesc}>Varios baloes separados</span>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            )}

            {/* ── Separador: Respostas a templates ─────────── */}
            <div style={{ borderTop: "1px solid " + W(0.06), margin: "16px 0 12px" }} />

            {/* Toggle: Assumir respostas a templates (backend: TemplateReplyHandoffService) */}
            <div className={classes.configField}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={classes.configLabel}>Assumir respostas a templates</span>
                  <Tooltip
                    title="Quando ativo, se o cliente responder a um template enviado por um atendente, a IA assume a conversa e responde já sabendo o que o template dizia. Desligado, a conversa continua com o atendente que enviou o template."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div
                  style={{
                    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                    background: nodeData.takeOverTemplateReplies ? "#5B7CFF" : W(0.1),
                    position: "relative", transition: "background 0.2s"
                  }}
                  onClick={function () {
                    updateNodeData("takeOverTemplateReplies", !nodeData.takeOverTemplateReplies);
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 8, background: "#fff",
                    position: "absolute", top: 2, transition: "left 0.2s",
                    left: nodeData.takeOverTemplateReplies ? 18 : 2
                  }} />
                </div>
              </div>
              {nodeData.takeOverTemplateReplies && (
                <div style={{
                  marginTop: 8, padding: "8px 10px", borderRadius: 6,
                  background: "rgba(91,124,255,0.06)",
                  border: "1px solid rgba(91,124,255,0.18)",
                  fontSize: 10, lineHeight: 1.4,
                  color: W(0.6),
                }}>
                  A IA assume só a primeira resposta do cliente, em até 7 dias depois do template. Se o template foi enviado no meio de uma conversa (cliente escreveu nas 24 h anteriores), a conversa continua com o atendente. Para retomar uma conversa que a IA assumiu, basta o atendente escrever nela.
                </div>
              )}
            </div>

            {/* ── Separador: Transferencia ─────────── */}
            <div style={{ borderTop: "1px solid " + W(0.06), margin: "16px 0 12px" }} />

            {/* Toggle: Transferir para atendente */}
            <div className={classes.configField}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={classes.configLabel}>Transferir para atendente</span>
                  <Tooltip
                    title={'A IA passa a conversa para uma pessoa da equipe quando acontecer o que você descrever em "Quando transferir?".'}
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div
                  style={{
                    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                    background: nodeData.enableEscalation ? "#5B7CFF" : W(0.1),
                    position: "relative", transition: "background 0.2s"
                  }}
                  onClick={function () {
                    updateNodeData("enableEscalation", !nodeData.enableEscalation);
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 8, background: "#fff",
                    position: "absolute", top: 2, transition: "left 0.2s",
                    left: nodeData.enableEscalation ? 18 : 2
                  }} />
                </div>
              </div>
            </div>

            {/* Conteudo de escalation (visivel somente quando enableEscalation) */}
            {nodeData.enableEscalation && (function () {
              // Resolve lista de atendentes selecionados aceitando legado
              // (escalateToUserId singular) para nao quebrar fluxos antigos.
              var selectedIds = (function () {
                var fromArr = Array.isArray(nodeData.escalateToUserIds) ? nodeData.escalateToUserIds : [];
                if (fromArr.length > 0) return fromArr;
                return nodeData.escalateToUserId ? [nodeData.escalateToUserId] : [];
              })();
              var escalationMode = ["random", "balanced", "percentage", "context"].indexOf(nodeData.escalationMode) >= 0
                ? nodeData.escalationMode
                : "random";
              // Fila de espera (bloco 3b): a conversa volta para "Aguardando", sem
              // atendente; os atendentes, a divisão e os percentuais não valem.
              var toTeam = nodeData.escalationTarget === "team";
              var escalationPercentages = (nodeData.escalationPercentages && typeof nodeData.escalationPercentages === "object")
                ? nodeData.escalationPercentages
                : {};
              var escalationContextMap = (nodeData.escalationContextMap && typeof nodeData.escalationContextMap === "object")
                ? nodeData.escalationContextMap
                : {};
              var contextEntries = Object.keys(escalationContextMap).map(function (tag) {
                return { tag: tag, userId: escalationContextMap[tag] };
              });
              var percentTotal = selectedIds.reduce(function (sum, id) {
                var v = escalationPercentages[String(id)] || escalationPercentages[id] || 0;
                return sum + (typeof v === "number" ? v : 0);
              }, 0);

              var toggleUser = function (u) {
                var isSelected = selectedIds.indexOf(u.id) >= 0;
                var next = isSelected
                  ? selectedIds.filter(function (id) { return id !== u.id; })
                  : selectedIds.concat([u.id]);
                updateNodeData("escalateToUserIds", next);
                // Limpa legado para evitar inconsistencia
                if (nodeData.escalateToUserId) {
                  updateNodeData("escalateToUserId", null);
                }
                // Se desmarcou, remove o percentual associado
                if (isSelected && escalationPercentages.hasOwnProperty(String(u.id))) {
                  var cleaned = {};
                  Object.keys(escalationPercentages).forEach(function (k) {
                    if (k !== String(u.id)) cleaned[k] = escalationPercentages[k];
                  });
                  updateNodeData("escalationPercentages", cleaned);
                }
                // Se desmarcou, remove entradas órfãs do mapa de contexto
                if (isSelected) {
                  var cleanedCtx = {};
                  var dirty = false;
                  Object.keys(escalationContextMap).forEach(function (tag) {
                    if (escalationContextMap[tag] !== u.id) {
                      cleanedCtx[tag] = escalationContextMap[tag];
                    } else {
                      dirty = true;
                    }
                  });
                  if (dirty) {
                    updateNodeData("escalationContextMap", cleanedCtx);
                  }
                }
              };

              return (
              <React.Fragment>
                {/* Para quem: os atendentes escolhidos ou a fila de espera. */}
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Para quem</span>
                  </div>
                  <div role="radiogroup" aria-label="Para quem transferir" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {[
                      { value: "users", label: "Atendentes escolhidos", desc: "A IA passa para um dos atendentes marcados abaixo." },
                      { value: "team", label: "Fila de espera", desc: "A conversa volta para Aguardando, sem atendente. Quem estiver livre pega." },
                    ].map(function (opt) {
                      var isOn = (opt.value === "team") === toTeam;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="radio"
                          aria-checked={isOn}
                          onClick={function () { updateNodeData("escalationTarget", opt.value === "team" ? "team" : null); }}
                          style={{
                            display: "flex", alignItems: "flex-start", gap: 8, width: "100%",
                            padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                            textAlign: "left", font: "inherit",
                            background: isOn ? "rgba(91,124,255,0.12)" : W(0.02),
                            border: isOn ? "1px solid rgba(91,124,255,0.35)" : "1px solid " + W(0.06),
                            transition: "all 0.15s",
                          }}
                        >
                          <span style={{
                            width: 14, height: 14, borderRadius: "50%",
                            border: isOn ? "4px solid #5B7CFF" : "2px solid " + W(0.20),
                            background: isOn ? "#fff" : "transparent",
                            marginTop: 2, flexShrink: 0, boxSizing: "border-box",
                            transition: "all 0.15s",
                          }} />
                          <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: isOn ? (isDark ? "#fff" : "#111318") : W(0.78) }}>
                              {opt.label}
                            </span>
                            <span style={{ fontSize: 10, color: W(0.45) }}>{opt.desc}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Seletor de atendentes (multiplo) */}
                {!toTeam && (
                  <div className={classes.configField}>
                    <div className={classes.configLabelRow}>
                      <span className={classes.configLabel}>Atendentes destino</span>
                      <Tooltip
                        title="Selecione um ou mais atendentes. Se marcar 2 ou mais, escolha um modo de distribuição abaixo."
                        placement="top"
                        classes={{ tooltip: classes.configInfoTooltip }}
                      >
                        <span className={classes.configInfoIcon}>
                          <Info size={13} />
                        </span>
                      </Tooltip>
                    </div>
                    <div style={{
                      maxHeight: 160, overflowY: "auto", marginTop: 6,
                      borderRadius: 8, border: "1px solid " + W(0.08)
                    }}>
                      {users.length === 0 && (
                        <div style={{ padding: 12, fontSize: 11, color: W(0.4), textAlign: "center" }}>
                          Nenhum usuário encontrado
                        </div>
                      )}
                      {users.map(function (u) {
                        var isSelected = selectedIds.indexOf(u.id) >= 0;
                        return (
                          <div
                            key={u.id}
                            style={{
                              display: "flex", alignItems: "center", gap: 8,
                              padding: "8px 12px", cursor: "pointer",
                              background: isSelected ? "rgba(91,124,255,0.12)" : "transparent",
                              borderBottom: "1px solid " + W(0.04),
                              transition: "background 0.15s"
                            }}
                            onClick={function () { toggleUser(u); }}
                          >
                            <div style={{
                              width: 18, height: 18, borderRadius: 4,
                              border: isSelected ? "2px solid #5B7CFF" : "2px solid " + W(0.15),
                              background: isSelected ? "#5B7CFF" : "transparent",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              transition: "all 0.15s", flexShrink: 0
                            }}>
                              {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                            </div>
                            <span style={{ fontSize: 12, color: isSelected ? "#fff" : W(0.7) }}>
                              {u.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Modo de distribuição (aparece com 2+ atendentes) */}
                {!toTeam && selectedIds.length >= 2 && (
                  <div className={classes.configField}>
                    <div className={classes.configLabelRow}>
                      <span className={classes.configLabel}>Modo de distribuição</span>
                      <Tooltip
                        title="Como a IA escolhe entre os atendentes selecionados a cada transferência."
                        placement="top"
                        classes={{ tooltip: classes.configInfoTooltip }}
                      >
                        <span className={classes.configInfoIcon}>
                          <Info size={13} />
                        </span>
                      </Tooltip>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                      {[
                        { value: "random",     label: "Aleatório",  desc: "Sorteia um atendente a cada transferência" },
                        { value: "balanced",   label: "Balanceado", desc: "Escolhe quem tem menos tickets em atendimento agora" },
                        { value: "percentage", label: "Percentual", desc: "Distribui conforme % configurado abaixo" },
                        { value: "context",    label: "Por contexto", desc: "Roteia conforme tag definida no prompt (fallback balanceado)" },
                      ].map(function (opt) {
                        var isOn = escalationMode === opt.value;
                        return (
                          <div
                            key={opt.value}
                            onClick={function () { updateNodeData("escalationMode", opt.value); }}
                            style={{
                              display: "flex", alignItems: "flex-start", gap: 8,
                              padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                              background: isOn ? "rgba(91,124,255,0.12)" : W(0.02),
                              border: isOn ? "1px solid rgba(91,124,255,0.35)" : "1px solid " + W(0.06),
                              transition: "all 0.15s",
                            }}
                          >
                            <div style={{
                              width: 14, height: 14, borderRadius: "50%",
                              border: isOn ? "4px solid #5B7CFF" : "2px solid " + W(0.20),
                              background: isOn ? "#fff" : "transparent",
                              marginTop: 2, flexShrink: 0,
                              transition: "all 0.15s",
                            }} />
                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: isOn ? "#fff" : W(0.78) }}>
                                {opt.label}
                              </span>
                              <span style={{ fontSize: 10, color: W(0.45) }}>{opt.desc}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Mapa tag → atendente (aparece com mode=context) */}
                {!toTeam && selectedIds.length >= 2 && escalationMode === "context" && (
                  <div className={classes.configField}>
                    <div className={classes.configLabelRow}>
                      <span className={classes.configLabel}>Mapa de roteamento por contexto</span>
                      <Tooltip
                        title="Cada tag aponta para 1 atendente. Use a tag no prompt: transferir_para_atendente(motivo='...', contexto='nome-da-tag')."
                        placement="top"
                        classes={{ tooltip: classes.configInfoTooltip }}
                      >
                        <span className={classes.configInfoIcon}>
                          <Info size={13} />
                        </span>
                      </Tooltip>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      {contextEntries.length === 0 && (
                        <div style={{
                          padding: "10px 12px", fontSize: 11,
                          color: W(0.4), textAlign: "center",
                          border: "1px dashed " + W(0.08), borderRadius: 6
                        }}>
                          Nenhuma tag configurada — adicione ao menos uma para roteamento contextual.
                        </div>
                      )}
                      {contextEntries.map(function (entry, idx) {
                        var duplicated = contextEntries.filter(function (e) {
                          return e.tag.trim() && e.tag === entry.tag;
                        }).length > 1;
                        var emptyTag = entry.tag.trim().length === 0;
                        var hasWarning = duplicated || emptyTag;
                        return (
                          <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <input
                              className={classes.configInput}
                              type="text"
                              placeholder="tag-em-kebab-case"
                              value={entry.tag}
                              onChange={function (e) {
                                var newTag = e.target.value;
                                var next = {};
                                contextEntries.forEach(function (it, i) {
                                  var key = i === idx ? newTag : it.tag;
                                  if (key !== undefined) {
                                    next[key] = it.userId;
                                  }
                                });
                                updateNodeData("escalationContextMap", next);
                              }}
                              style={{
                                flex: 1,
                                borderColor: hasWarning ? "rgba(245,158,11,0.6)" : undefined
                              }}
                            />
                            <select
                              className={classes.configInput}
                              value={entry.userId}
                              onChange={function (e) {
                                var newUserId = parseInt(e.target.value, 10);
                                var next = Object.assign({}, escalationContextMap);
                                next[entry.tag] = newUserId;
                                updateNodeData("escalationContextMap", next);
                              }}
                              style={{ flex: 1 }}
                            >
                              {selectedIds.map(function (uid) {
                                var user = users.find(function (u) { return u.id === uid; });
                                return (
                                  <option key={uid} value={uid}>
                                    {user ? user.name : ("ID " + uid)}
                                  </option>
                                );
                              })}
                            </select>
                            <button
                              type="button"
                              onClick={function () {
                                var next = {};
                                contextEntries.forEach(function (it, i) {
                                  if (i !== idx) next[it.tag] = it.userId;
                                });
                                updateNodeData("escalationContextMap", next);
                              }}
                              style={{
                                background: "transparent",
                                border: "1px solid " + W(0.12),
                                borderRadius: 6,
                                color: W(0.5),
                                padding: "6px 8px",
                                cursor: "pointer",
                                fontSize: 12
                              }}
                              title="Remover esta tag"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        onClick={function () {
                          // Sugere nova tag única — chave vazia inicial,
                          // operador renomeia em seguida.
                          var seedTag = "";
                          var i = 0;
                          while (escalationContextMap.hasOwnProperty(seedTag)) {
                            i += 1;
                            seedTag = "nova-tag-" + i;
                          }
                          var next = Object.assign({}, escalationContextMap);
                          next[seedTag] = selectedIds[0];
                          updateNodeData("escalationContextMap", next);
                        }}
                        style={{
                          marginTop: 4,
                          background: "rgba(91,124,255,0.10)",
                          border: "1px solid rgba(91,124,255,0.30)",
                          borderRadius: 6,
                          color: "#5B7CFF",
                          padding: "6px 10px",
                          cursor: "pointer",
                          fontSize: 12,
                          fontWeight: 600
                        }}
                      >
                        + Adicionar tag
                      </button>
                      {(contextEntries.some(function (e) { return e.tag.trim().length === 0; }) ||
                        contextEntries.some(function (e, i) {
                          return e.tag.trim() && contextEntries.filter(function (x) { return x.tag === e.tag; }).length > 1;
                        })) && (
                        <div style={{
                          marginTop: 6, padding: "6px 8px", borderRadius: 6,
                          fontSize: 11, fontWeight: 600,
                          color: "#f59e0b",
                          background: "rgba(245,158,11,0.06)",
                          border: "1px solid rgba(245,158,11,0.18)"
                        }}>
                          Há tags vazias ou duplicadas — corrija para que o roteamento funcione.
                        </div>
                      )}
                      <div style={{
                        marginTop: 8, padding: "6px 8px", borderRadius: 6,
                        fontSize: 11, color: W(0.55),
                        background: W(0.02),
                        border: "1px solid " + W(0.05)
                      }}>
                        No prompt, instrua a IA: <code style={{ color: W(0.75) }}>transferir_para_atendente(motivo='...', contexto='nome-da-tag')</code>. Quando a IA omitir <code>contexto</code>, a transferência cai no balanceamento padrão.
                      </div>
                    </div>
                  </div>
                )}

                {/* Inputs de percentual (aparece com mode=percentage) */}
                {!toTeam && selectedIds.length >= 2 && escalationMode === "percentage" && (
                  <div className={classes.configField}>
                    <div className={classes.configLabelRow}>
                      <span className={classes.configLabel}>Percentuais</span>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      {selectedIds.map(function (id) {
                        var user = users.find(function (u) { return u.id === id; });
                        var pct = (escalationPercentages[String(id)] || escalationPercentages[id] || 0);
                        return (
                          <div key={id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <span style={{ flex: 1, fontSize: 12, color: W(0.78) }}>
                              {user ? user.name : ("ID " + id)}
                            </span>
                            <input
                              className={classes.configInput}
                              type="number"
                              min="0"
                              max="100"
                              step="5"
                              value={pct}
                              onChange={function (e) {
                                var val = parseInt(e.target.value, 10);
                                if (isNaN(val)) val = 0;
                                if (val < 0) val = 0;
                                if (val > 100) val = 100;
                                var next = Object.assign({}, escalationPercentages);
                                next[String(id)] = val;
                                updateNodeData("escalationPercentages", next);
                              }}
                              style={{ width: 70, textAlign: "right" }}
                            />
                            <span style={{ fontSize: 11, color: W(0.4) }}>%</span>
                          </div>
                        );
                      })}
                      <div style={{
                        marginTop: 6, padding: "6px 8px", borderRadius: 6,
                        fontSize: 11, fontWeight: 600,
                        color: percentTotal === 100 ? "#22c55e" : "#f59e0b",
                        background: percentTotal === 100 ? "rgba(34,197,94,0.06)" : "rgba(245,158,11,0.06)",
                        border: percentTotal === 100 ? "1px solid rgba(34,197,94,0.18)" : "1px solid rgba(245,158,11,0.18)",
                      }}>
                        Total: {percentTotal}% {percentTotal !== 100 && "— ajuste para somar 100%"}
                      </div>
                    </div>
                  </div>
                )}

                {/* Condicao de transferencia */}
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Quando transferir?</span>
                    <Tooltip
                      title="Descreva em linguagem natural as situacoes em que a IA deve transferir para o atendente. Ex: quando o cliente estiver irritado, quiser reclamar, pedir para falar com humano, etc."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  <div style={{ position: "relative" }}>
                    <textarea
                      className={classes.configTextarea}
                      value={nodeData.escalationCondition || ""}
                      onChange={function (e) {
                        updateNodeData("escalationCondition", e.target.value);
                      }}
                      placeholder={"Transferir para o atendente quando:\n- O cliente demonstrar irritacao ou insatisfacao\n- O cliente pedir para falar com um humano\n- O cliente quiser fazer uma reclamacao"}
                      style={{ minHeight: 90, paddingRight: 40 }}
                    />
                    <Tooltip title="Reformular com IA" placement="top">
                      <div
                        style={{
                          position: "absolute", top: 8, right: 8,
                          width: 28, height: 28, borderRadius: 6,
                          background: nodeData._reformulating ? "rgba(91,124,255,0.3)" : "rgba(91,124,255,0.15)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: nodeData._reformulating ? "wait" : "pointer",
                          transition: "background 0.2s"
                        }}
                        onClick={function () {
                          if (nodeData._reformulating) return;
                          var text = nodeData.escalationCondition;
                          if (!text || !text.trim()) return;
                          updateNodeData("_reformulating", true);
                          api.post("/ai-agents/reformulate-prompt", {
                            prompt: "Reformule o seguinte texto de instrucao de transferencia para ficar mais claro e estruturado para uma IA de atendimento entender exatamente quando deve transferir o cliente para um atendente humano. Mantenha em portugues, seja objetivo:\n\n" + text
                          })
                          .then(function (res) {
                            if (res.data && res.data.reformulated) {
                              updateNodeData("escalationCondition", res.data.reformulated);
                            }
                          })
                          .catch(function (err) { if (props.toastError) props.toastError(err); })
                          .finally(function () {
                            updateNodeData("_reformulating", false);
                          });
                        }}
                      >
                        {nodeData._reformulating
                          ? <RefreshCw size={13} color="#5B7CFF" style={{ animation: "spin 1s linear infinite" }} />
                          : <Sparkles size={13} color="#5B7CFF" />
                        }
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </React.Fragment>
              );
            })()}

            {/* ── Separador: Encerramento automático ─────────── */}
            <div style={{ borderTop: "1px solid " + W(0.06), margin: "16px 0 12px" }} />

            {/* Toggle: Permitir IA encerrar atendimento */}
            <div className={classes.configField}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={classes.configLabel}>Encerrar atendimento</span>
                  <Tooltip
                    title="Quando ativo, a IA pode encerrar o ticket sozinha quando o cliente confirmar explicitamente que não precisa de mais nada (ex: 'só isso', 'obrigado', 'pode encerrar')."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div
                  style={{
                    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                    background: nodeData.enableAutoResolve ? "#5B7CFF" : W(0.1),
                    position: "relative", transition: "background 0.2s"
                  }}
                  onClick={function () {
                    updateNodeData("enableAutoResolve", !nodeData.enableAutoResolve);
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 8, background: "#fff",
                    position: "absolute", top: 2, transition: "left 0.2s",
                    left: nodeData.enableAutoResolve ? 18 : 2
                  }} />
                </div>
              </div>
              {nodeData.enableAutoResolve && (
                <div style={{
                  marginTop: 8, padding: "8px 10px", borderRadius: 6,
                  background: "rgba(91,124,255,0.06)",
                  border: "1px solid rgba(91,124,255,0.18)",
                  fontSize: 10, lineHeight: 1.4,
                  color: W(0.6),
                }}>
                  A IA enviará uma despedida curta e fechará o ticket automaticamente quando o cliente confirmar que não precisa de mais nada.
                </div>
              )}
            </div>

            {/* ── Separador: Tools avancadas ─────────── */}
            <div style={{ borderTop: "1px solid " + W(0.06), margin: "16px 0 12px" }} />

            {/* Toggle: Transferir para fila */}
            <div className={classes.configField}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={classes.configLabel}>Transferir para fila</span>
                  <Tooltip
                    title="Quando ativo, a IA pode transferir o ticket para uma FILA inteira (sem escolher atendente específico). A lista de filas disponíveis é enviada automaticamente para a IA — ela escolhe pelo nome."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div
                  style={{
                    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                    background: nodeData.enableTransferToQueue ? "#5B7CFF" : W(0.1),
                    position: "relative", transition: "background 0.2s"
                  }}
                  onClick={function () {
                    updateNodeData("enableTransferToQueue", !nodeData.enableTransferToQueue);
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 8, background: "#fff",
                    position: "absolute", top: 2, transition: "left 0.2s",
                    left: nodeData.enableTransferToQueue ? 18 : 2
                  }} />
                </div>
              </div>
              {nodeData.enableTransferToQueue && (
                <div style={{
                  marginTop: 8, padding: "8px 10px", borderRadius: 6,
                  background: "rgba(91,124,255,0.06)",
                  border: "1px solid rgba(91,124,255,0.18)",
                  fontSize: 10, lineHeight: 1.4,
                  color: W(0.6),
                }}>
                  Todas as filas ativas da empresa serão oferecidas à IA. Útil para casos como "vou direcionar para a equipe Comercial".
                </div>
              )}
            </div>

            {/* Toggle: Etiquetar atendimento */}
            <div className={classes.configField}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={classes.configLabel}>Etiquetar atendimento</span>
                  <Tooltip
                    title="Quando ativo, a IA pode aplicar etiquetas (tags) ao ticket para categorização e analytics. Apenas etiquetas cadastradas na empresa serão utilizadas — nomes inexistentes são ignorados."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div
                  style={{
                    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                    background: nodeData.enableTagTicket ? "#5B7CFF" : W(0.1),
                    position: "relative", transition: "background 0.2s"
                  }}
                  onClick={function () {
                    updateNodeData("enableTagTicket", !nodeData.enableTagTicket);
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 8, background: "#fff",
                    position: "absolute", top: 2, transition: "left 0.2s",
                    left: nodeData.enableTagTicket ? 18 : 2
                  }} />
                </div>
              </div>
              {nodeData.enableTagTicket && (
                <div style={{
                  marginTop: 8, padding: "8px 10px", borderRadius: 6,
                  background: "rgba(91,124,255,0.06)",
                  border: "1px solid rgba(91,124,255,0.18)",
                  fontSize: 10, lineHeight: 1.4,
                  color: W(0.6),
                }}>
                  A IA receberá a lista de etiquetas cadastradas em Tags. Ação silenciosa — o cliente não percebe.
                </div>
              )}
            </div>

            {/* Toggle: Adicionar nota interna */}
            <div className={classes.configField}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={classes.configLabel}>Adicionar nota interna</span>
                  <Tooltip
                    title="Quando ativo, a IA pode registrar notas internas no ticket (visíveis apenas para atendentes). Útil para deixar contexto antes de transferir o atendimento."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div
                  style={{
                    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                    background: nodeData.enableAddNote ? "#5B7CFF" : W(0.1),
                    position: "relative", transition: "background 0.2s"
                  }}
                  onClick={function () {
                    updateNodeData("enableAddNote", !nodeData.enableAddNote);
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 8, background: "#fff",
                    position: "absolute", top: 2, transition: "left 0.2s",
                    left: nodeData.enableAddNote ? 18 : 2
                  }} />
                </div>
              </div>
              {nodeData.enableAddNote && (
                <div style={{
                  marginTop: 8, padding: "8px 10px", borderRadius: 6,
                  background: "rgba(91,124,255,0.06)",
                  border: "1px solid rgba(91,124,255,0.18)",
                  fontSize: 10, lineHeight: 1.4,
                  color: W(0.6),
                }}>
                  A IA deixará um resumo estruturado antes de transferir, dando contexto ao atendente. Cliente não recebe a nota.
                </div>
              )}
            </div>

            {agentMode === "custom" && (
              <React.Fragment>
                {/* Separador: Processamento de Mídia */}
                <div style={{
                  borderTop: "1px solid " + W(0.06),
                  margin: "16px 0 12px",
                  paddingTop: 12,
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  color: W(0.7),
                  textTransform: "uppercase",
                }}>
                  Processamento de Mídia
                </div>

                {/* Toggle: Processar imagens */}
                <div className={classes.configField}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span className={classes.configLabel}>Processar imagens recebidas</span>
                      <Tooltip
                        title="Quando ativo, fotos enviadas pelo cliente são analisadas pelo modelo de visão (custo extra). Quando inativo, a IA recebe apenas '[O cliente enviou uma imagem...]' como texto e pede para o cliente descrever."
                        placement="top"
                        classes={{ tooltip: classes.configInfoTooltip }}
                      >
                        <span className={classes.configInfoIcon}>
                          <Info size={13} />
                        </span>
                      </Tooltip>
                    </div>
                    <div
                      style={{
                        width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                        background: nodeData.processImages ? "#5B7CFF" : W(0.1),
                        position: "relative", transition: "background 0.2s"
                      }}
                      onClick={function () {
                        updateNodeData("processImages", !nodeData.processImages);
                      }}
                    >
                      <div style={{
                        width: 16, height: 16, borderRadius: 8, background: "#fff",
                        position: "absolute", top: 2, transition: "left 0.2s",
                        left: nodeData.processImages ? 18 : 2
                      }} />
                    </div>
                  </div>
                </div>

                {/* Toggle: Transcrever áudio */}
                <div className={classes.configField}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span className={classes.configLabel}>Transcrever áudios recebidos</span>
                      <Tooltip
                        title="Quando ativo, áudios são transcritos automaticamente via Whisper (custo extra). Quando inativo, a IA recebe '[O cliente enviou um áudio. Peça para escrever em texto.]' e orienta o cliente a digitar."
                        placement="top"
                        classes={{ tooltip: classes.configInfoTooltip }}
                      >
                        <span className={classes.configInfoIcon}>
                          <Info size={13} />
                        </span>
                      </Tooltip>
                    </div>
                    <div
                      style={{
                        width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                        background: nodeData.processAudio ? "#5B7CFF" : W(0.1),
                        position: "relative", transition: "background 0.2s"
                      }}
                      onClick={function () {
                        updateNodeData("processAudio", !nodeData.processAudio);
                      }}
                    >
                      <div style={{
                        width: 16, height: 16, borderRadius: 8, background: "#fff",
                        position: "absolute", top: 2, transition: "left 0.2s",
                        left: nodeData.processAudio ? 18 : 2
                      }} />
                    </div>
                  </div>
                </div>

                {/* Toggle: Processar vídeo */}
                <div className={classes.configField}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span className={classes.configLabel}>Processar vídeos recebidos</span>
                      <Tooltip
                        title="Configuração preparada para análise futura de vídeo. Hoje, vídeos sempre viram '[O cliente enviou um vídeo. Peça para descrever em texto.]' — independente deste toggle."
                        placement="top"
                        classes={{ tooltip: classes.configInfoTooltip }}
                      >
                        <span className={classes.configInfoIcon}>
                          <Info size={13} />
                        </span>
                      </Tooltip>
                    </div>
                    <div
                      style={{
                        width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                        background: nodeData.processVideo ? "#5B7CFF" : W(0.1),
                        position: "relative", transition: "background 0.2s"
                      }}
                      onClick={function () {
                        updateNodeData("processVideo", !nodeData.processVideo);
                      }}
                    >
                      <div style={{
                        width: 16, height: 16, borderRadius: 8, background: "#fff",
                        position: "absolute", top: 2, transition: "left 0.2s",
                        left: nodeData.processVideo ? 18 : 2
                      }} />
                    </div>
                  </div>
                </div>
              </React.Fragment>
            )}
          </React.Fragment>
        )}

        {cfg.type === "tool_files" && (function () {
          var files = nodeData.files || [];
          var fileInputRef = React.createRef();

          var MAX_FILE_SIZE = 50 * 1024 * 1024;
          var ACCEPTED_TYPES = "image/*,application/pdf,audio/*";

          var formatFileSize = function (bytes) {
            if (bytes < 1024) return bytes + " B";
            if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
            return (bytes / (1024 * 1024)).toFixed(1) + " MB";
          };

          var handleFileSelected = function (fileObj) {
            if (!fileObj) return;
            if (fileObj.size > MAX_FILE_SIZE) {
              toastError({ response: { data: { error: "Arquivo excede 50MB" } } });
              return;
            }
            var isValid = fileObj.type.startsWith("image/") || fileObj.type === "application/pdf" || fileObj.type.startsWith("audio/");
            if (!isValid) {
              toastError({ response: { data: { error: "Tipo nao suportado. Use imagem, PDF ou audio." } } });
              return;
            }
            setPendingFile(fileObj);
          };

          var handleAdd = function () {
            var name = pendingFileName.trim();
            if (!name) {
              toastError({ response: { data: { error: "Digite um nome para o arquivo" } } });
              return;
            }
            if (!pendingFile) {
              toastError({ response: { data: { error: "Selecione um arquivo" } } });
              return;
            }

            setFileUploading(true);
            var formData = new FormData();
            formData.append("file", pendingFile);
            formData.append("fileName", name);

            api.post("/workflows/upload", formData, {
              headers: { "Content-Type": "multipart/form-data" },
            })
              .then(function (res) {
                var newFile = {
                  id: res.data.id || Date.now() + "-" + Math.random().toString(36).substr(2, 9),
                  name: name,
                  originalName: pendingFile.name,
                  size: pendingFile.size,
                  type: pendingFile.type,
                  url: res.data.url || res.data.path,
                };
                updateNodeData("files", files.concat(newFile));
                setPendingFileName("");
                setPendingFile(null);
              })
              .catch(function (err) {
                toastError(err);
              })
              .finally(function () {
                setFileUploading(false);
              });
          };

          var removeFile = function (fileId) {
            updateNodeData("files", files.filter(function (f) { return f.id !== fileId; }));
          };

          return (
            <React.Fragment>
              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Nome do arquivo</span>
                  <Tooltip
                    title="De um nome unico para identificar este arquivo. Ex: Catalogo 2026, Tabela de precos, etc."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <input
                  className={classes.configInput}
                  value={pendingFileName}
                  onChange={function (e) { setPendingFileName(e.target.value); }}
                  placeholder="ex: Catalogo de produtos 2026"
                  style={{ width: "100%" }}
                />
              </div>

              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Arquivo</span>
                  <Tooltip
                    title="Selecione imagem, PDF ou audio (ate 50MB)."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_TYPES}
                  style={{ display: "none" }}
                  onChange={function (e) {
                    if (e.target.files && e.target.files[0]) {
                      handleFileSelected(e.target.files[0]);
                    }
                    e.target.value = "";
                  }}
                />

                <div
                  className={classes.configUploadZone}
                  onClick={function () { fileInputRef.current && fileInputRef.current.click(); }}
                  style={{ padding: pendingFile ? "12px 16px" : "20px 16px" }}
                >
                  {pendingFile ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                      <div className={classes.configFileIcon}>
                        <FileUp size={13} color="#f59e0b" strokeWidth={2} />
                      </div>
                      <div className={classes.configFileInfo}>
                        <div className={classes.configFileName}>{pendingFile.name}</div>
                        <div className={classes.configFileSize}>{formatFileSize(pendingFile.size)}</div>
                      </div>
                      <span
                        className={classes.configFileRemove}
                        onClick={function (e) { e.stopPropagation(); setPendingFile(null); }}
                      >
                        <X size={14} />
                      </span>
                    </div>
                  ) : (
                    <React.Fragment>
                      <div className={classes.configUploadIcon}>
                        <Upload size={18} color="#f59e0b" strokeWidth={2} />
                      </div>
                      <div className={classes.configUploadText}>
                        Clique para selecionar
                      </div>
                      <div className={classes.configUploadHint}>
                        Imagem, PDF ou audio — ate 50MB
                      </div>
                    </React.Fragment>
                  )}
                </div>
              </div>

              <div className={classes.configField}>
                <button
                  className={classes.saveBtn}
                  onClick={handleAdd}
                  disabled={fileUploading || !pendingFileName.trim() || !pendingFile}
                  style={{ width: "100%", justifyContent: "center" }}
                >
                  {fileUploading ? (
                    <React.Fragment>
                      <CircularProgress size={14} style={{ color: "#fff" }} />
                      Enviando...
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <Plus size={14} />
                      Adicionar arquivo
                    </React.Fragment>
                  )}
                </button>
              </div>

              {files.length > 0 && (
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Arquivos adicionados ({files.length})</span>
                  </div>
                  <div className={classes.configFileList}>
                    {files.map(function (file) {
                      return (
                        <div key={file.id} className={classes.configFileItem}>
                          <div className={classes.configFileIcon}>
                            <FileUp size={13} color="#f59e0b" strokeWidth={2} />
                          </div>
                          <div className={classes.configFileInfo}>
                            <div className={classes.configFileName}>{file.name}</div>
                            <div className={classes.configFileSize}>
                              {file.originalName} — {formatFileSize(file.size)}
                            </div>
                          </div>
                          <span
                            className={classes.configFileRemove}
                            onClick={function () { removeFile(file.id); }}
                          >
                            <Trash2 size={12} />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })()}

        {cfg.type === "tool_memory" && (function () {
          var memories = nodeData.memories || [];
          var editingId = memEditingId;
          var setEditingId = setMemEditingId;
          var expandedId = memExpandedId;
          var setExpandedId = setMemExpandedId;
          var generating = memGenerating;

          var generateSummaryFn = function () {
            if (!memContent.trim()) return;
            setMemGenerating(true);
            api.post("/workflows/generate-summary", { content: memContent })
              .then(function (res) {
                setMemSummary(res.data.summary || "");
              })
              .catch(function () {
                setMemSummary(memContent.substring(0, 120) + (memContent.length > 120 ? "..." : ""));
              })
              .finally(function () {
                setMemGenerating(false);
              });
          };

          var addMemory = function () {
            if (!memName.trim()) {
              toastError({ response: { data: { error: "Digite um nome para a memoria" } } });
              return;
            }
            if (!memContent.trim()) {
              toastError({ response: { data: { error: "Digite o conteudo da memoria" } } });
              return;
            }
            var summary = memSummary.trim() || memContent.substring(0, 120) + (memContent.length > 120 ? "..." : "");

            if (editingId) {
              var updated = memories.map(function (m) {
                if (m.id !== editingId) return m;
                return Object.assign({}, m, { name: memName.trim(), content: memContent.trim(), summary: summary });
              });
              updateNodeData("memories", updated);
              setEditingId(null);
            } else {
              var newMem = {
                id: Date.now() + "-" + Math.random().toString(36).substr(2, 9),
                name: memName.trim(),
                content: memContent.trim(),
                summary: summary,
              };
              updateNodeData("memories", memories.concat(newMem));
            }

            setMemName("");
            setMemContent("");
            setMemSummary("");
          };

          var editMemory = function (mem) {
            setEditingId(mem.id);
            setMemName(mem.name);
            setMemContent(mem.content);
            setMemSummary(mem.summary || "");
          };

          var cancelEdit = function () {
            setEditingId(null);
            setMemName("");
            setMemContent("");
            setMemSummary("");
          };

          var removeMemory = function (memId) {
            updateNodeData("memories", memories.filter(function (m) { return m.id !== memId; }));
            if (editingId === memId) cancelEdit();
          };

          return (
            <React.Fragment>
              {/* Nome da memoria */}
              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>
                    {editingId ? "Editando memoria" : "Nome da memoria"}
                  </span>
                  <Tooltip
                    title="De um nome unico para identificar esta memoria. Ex: Politica de troca, Tabela de precos, Horario de funcionamento."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <input
                  className={classes.configInput}
                  value={memName}
                  onChange={function (e) { setMemName(e.target.value); }}
                  placeholder="ex: Politica de troca e devolucao"
                  style={{ width: "100%" }}
                />
              </div>

              {/* Conteudo do prompt */}
              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Conteudo (prompt)</span>
                  <Tooltip
                    title="Escreva aqui as instrucoes detalhadas para esse caso especifico. A IA vai ler esse conteudo quando identificar que o assunto e relacionado."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div className={classes.configTextareaWrap}>
                  <Tooltip title="Expandir" placement="top">
                    <div
                      className={classes.configExpandBtn}
                      onClick={function () { setMemPromptModalOpen(true); }}
                    >
                      <Maximize2 size={12} />
                    </div>
                  </Tooltip>
                  <textarea
                    className={classes.configTextarea}
                    value={memContent}
                    onChange={function (e) { setMemContent(e.target.value); }}
                    placeholder={"Quando o cliente perguntar sobre troca ou devolucao:\n- Prazo de 7 dias corridos apos o recebimento\n- Produto deve estar sem uso e na embalagem original\n- Enviar foto do produto + nota fiscal\n- Solicitar ao cliente o numero do pedido"}
                    style={{ minHeight: 120, paddingRight: 40 }}
                  />
                </div>
              </div>

              {/* Resumo */}
              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Resumo da memoria</span>
                  <Tooltip
                    title="Breve descricao usada pela IA para decidir se deve consultar esta memoria. Gerado automaticamente, mas voce pode editar."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                  <textarea
                    className={classes.configTextarea}
                    value={memSummary}
                    onChange={function (e) { setMemSummary(e.target.value); }}
                    placeholder="Clique em gerar para criar um resumo automatico..."
                    style={{ minHeight: 50, flex: 1 }}
                  />
                  <Tooltip title="Gerar resumo com IA" placement="top">
                    <button
                      className={classes.configAddBtn}
                      onClick={generateSummaryFn}
                      disabled={generating || !memContent.trim()}
                      type="button"
                      style={{ height: 36, width: 36, flexShrink: 0 }}
                    >
                      {generating ? (
                        <CircularProgress size={14} style={{ color: "#ec4899" }} />
                      ) : (
                        <Sparkles size={14} color="#ec4899" />
                      )}
                    </button>
                  </Tooltip>
                </div>
              </div>

              {/* Botao adicionar/salvar */}
              <div className={classes.configField}>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    className={classes.saveBtn}
                    onClick={addMemory}
                    disabled={!memName.trim() || !memContent.trim()}
                    style={{ flex: 1, justifyContent: "center" }}
                  >
                    {editingId ? (
                      <React.Fragment>
                        <Check size={14} />
                        Salvar alteracoes
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        <Plus size={14} />
                        Adicionar memoria
                      </React.Fragment>
                    )}
                  </button>
                  {editingId && (
                    <button
                      className={classes.configAddBtn}
                      onClick={cancelEdit}
                      type="button"
                      style={{ height: 36, width: 36 }}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* Lista de memorias */}
              {memories.length > 0 && (
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Memorias adicionadas ({memories.length})</span>
                  </div>
                  <div className={classes.configFileList}>
                    {memories.map(function (mem) {
                      var isExpanded = expandedId === mem.id;
                      return (
                        <div key={mem.id} style={{ marginBottom: 4 }}>
                          <div className={classes.configFileItem} style={{ cursor: "pointer" }} onClick={function () { setExpandedId(isExpanded ? null : mem.id); }}>
                            <div className={classes.configFileIcon} style={{ background: "#ec489918" }}>
                              <Brain size={13} color="#ec4899" strokeWidth={2} />
                            </div>
                            <div className={classes.configFileInfo}>
                              <div className={classes.configFileName}>{mem.name}</div>
                              <div className={classes.configFileSize} style={{ fontSize: 10, opacity: 0.5 }}>
                                {mem.summary || mem.content.substring(0, 60) + "..."}
                              </div>
                            </div>
                            <span style={{ opacity: 0.4, marginRight: 4, display: "flex" }}>
                              {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            </span>
                            <span
                              className={classes.configFileRemove}
                              onClick={function (e) { e.stopPropagation(); editMemory(mem); }}
                              style={{ marginRight: 2 }}
                            >
                              <Pencil size={11} />
                            </span>
                            <span
                              className={classes.configFileRemove}
                              onClick={function (e) { e.stopPropagation(); removeMemory(mem.id); }}
                            >
                              <Trash2 size={12} />
                            </span>
                          </div>
                          {isExpanded && (
                            <div style={{
                              padding: "8px 12px",
                              fontSize: 11,
                              lineHeight: 1.5,
                              color: W(0.7),
                              background: "rgba(236,72,153,0.05)",
                              borderRadius: "0 0 8px 8px",
                              borderTop: "1px solid rgba(236,72,153,0.1)",
                              whiteSpace: "pre-wrap",
                              maxHeight: 200,
                              overflowY: "auto",
                            }}>
                              {mem.content}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {memories.length === 0 && (
                <div className={classes.configField}>
                  <div className={classes.configEmpty}>
                    Nenhuma memoria adicionada
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })()}

        {cfg.type === "tool_websearch" && (function () {
          var domains = nodeData.domains || [];
          var domainInputRef = React.createRef();

          var formatDomain = function (raw) {
            var d = raw.trim().toLowerCase();
            d = d.replace(/^https?:\/\//, "");
            d = d.replace(/^www\./, "");
            d = d.replace(/\/.*$/, "");
            d = d.replace(/\s/g, "");
            return d;
          };

          var addDomain = function () {
            var input = domainInputRef.current;
            if (!input) return;
            var raw = input.value;
            if (!raw || !raw.trim()) return;
            var formatted = formatDomain(raw);
            if (!formatted || formatted.indexOf(".") === -1) return;
            if (domains.indexOf(formatted) !== -1) {
              input.value = "";
              return;
            }
            updateNodeData("domains", domains.concat(formatted));
            input.value = "";
          };

          var removeDomain = function (domain) {
            updateNodeData("domains", domains.filter(function (d) { return d !== domain; }));
          };

          var handleKeyDown = function (e) {
            if (e.key === "Enter") {
              e.preventDefault();
              addDomain();
            }
          };

          return (
            <React.Fragment>
              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Dominios permitidos</span>
                  <Tooltip
                    title="A IA so vai pesquisar nesses dominios. Adicione os sites confiaveis que a IA pode consultar. URLs sao formatadas automaticamente."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>

                <div className={classes.configInputRow}>
                  <input
                    ref={domainInputRef}
                    className={classes.configInput}
                    placeholder="ex: meusite.com.br"
                    onKeyDown={handleKeyDown}
                  />
                  <button
                    className={classes.configAddBtn}
                    onClick={addDomain}
                    type="button"
                  >
                    <Plus size={14} />
                  </button>
                </div>

                {domains.length > 0 && (
                  <div className={classes.configDomainList}>
                    {domains.map(function (domain) {
                      return (
                        <div key={domain} className={classes.configDomainItem}>
                          <Globe size={12} className={classes.configDomainIcon} />
                          <span className={classes.configDomainText}>{domain}</span>
                          <span
                            className={classes.configDomainRemove}
                            onClick={function () { removeDomain(domain); }}
                          >
                            <Trash2 size={12} />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {domains.length === 0 && (
                  <div className={classes.configEmpty}>
                    Nenhum dominio adicionado
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })()}

        {cfg.type === "tool_woocommerce" && (function () {
          var selectedIds = nodeData.integrationIds || [];
          var toggleIntegration = function (id) {
            var idx = selectedIds.indexOf(id);
            if (idx === -1) {
              updateNodeData("integrationIds", selectedIds.concat(id));
            } else {
              updateNodeData("integrationIds", selectedIds.filter(function (x) { return x !== id; }));
            }
          };

          var anyStale = selectedIds.some(function (id) {
            var integ = wooIntegrations.find(function (w) { return w.id === id; });
            if (!integ) return false;
            return !integ.lastSyncAt || integ.lastSyncStatus === "error";
          });

          return (
            <React.Fragment>
              <div className={classes.configField}>
                <div style={{
                  padding: "10px 12px",
                  background: "rgba(147, 51, 234, 0.08)",
                  borderRadius: 8,
                  fontSize: 11,
                  lineHeight: 1.5,
                  color: W(0.7),
                }}>
                  A IA podera buscar produtos no catalogo das lojas selecionadas durante a conversa.
                  O catalogo e sincronizado periodicamente no plano de fundo.
                </div>
              </div>

              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Lojas WooCommerce</span>
                  <Tooltip
                    title="Selecione uma ou mais lojas. A IA buscara produtos em todas elas e responder com o resultado mais relevante."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>

                {wooIntegrations.length === 0 && (
                  <div className={classes.configEmpty} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <AlertTriangle size={14} color="#f59e0b" />
                    <span>Nenhuma loja cadastrada. Cadastre em <strong>Configuracoes &gt; Integracoes</strong>.</span>
                  </div>
                )}

                {wooIntegrations.length > 0 && (
                  <div className={classes.configDropdown} style={{ maxHeight: 240 }}>
                    {wooIntegrations.map(function (integ) {
                      var isSelected = selectedIds.indexOf(integ.id) !== -1;
                      var inactive = integ.isActive === false;
                      var hasError = integ.lastSyncStatus === "error";
                      var neverSynced = !integ.lastSyncAt;
                      return (
                        <div
                          key={integ.id}
                          className={classes.configDropdownItem}
                          onClick={function () { if (!inactive) toggleIntegration(integ.id); }}
                          style={inactive ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                        >
                          <div
                            className={classes.configDropdownCheck + (isSelected ? " " + classes.configDropdownCheckActive : "")}
                          >
                            {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                          </div>
                          <ShoppingBag size={12} style={{ color: "#9333ea", flexShrink: 0 }} />
                          <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 12, fontWeight: 500 }}>{integ.name}</span>
                            <span style={{ fontSize: 10, color: W(0.4), display: "flex", gap: 6, alignItems: "center" }}>
                              {integ.productCount || 0} produtos
                              <span style={{ color: W(0.25) }}>•</span>
                              <span style={{ color: hasError ? "#ef4444" : neverSynced ? "#f59e0b" : W(0.4) }}>
                                {hasError ? "erro no ultimo sync" : "sync " + formatRelativeTime(integ.lastSyncAt, "nunca sincronizado")}
                              </span>
                              {inactive && (
                                <React.Fragment>
                                  <span style={{ color: W(0.25) }}>•</span>
                                  <span style={{ color: "#ef4444" }}>inativa</span>
                                </React.Fragment>
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {anyStale && (
                  <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: "#f59e0b" }}>
                    <AlertTriangle size={12} />
                    <span>Alguma loja selecionada nao sincronizou ainda — verifique em Integracoes.</span>
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })()}

        {/* ═══ CRIAR CARD CRM ═══ */}
        {cfg.type === "tool_crm_create" && (function () {
          var fields = nodeData.fields || [];
          var selectedBoardId = nodeData.boardId || null;

          var handleBoardChange = function (boardId) {
            updateNodeData("boardId", boardId);
          };

          var addField = function () {
            var name = crmFieldName.trim();
            if (!name) {
              toastError({ response: { data: { error: "Digite o nome do campo" } } });
              return;
            }
            if (fields.some(function (f) { return f.name.toLowerCase() === name.toLowerCase(); })) {
              toastError({ response: { data: { error: "Campo ja existe" } } });
              return;
            }
            var newField = {
              id: Date.now() + "-" + Math.random().toString(36).substr(2, 6),
              name: name,
            };
            updateNodeData("fields", fields.concat(newField));
            setCrmFieldName("");
          };

          var removeField = function (fieldId) {
            updateNodeData("fields", fields.filter(function (f) { return f.id !== fieldId; }));
          };

          return (
            <React.Fragment>
              <div className={classes.configField}>
                <div style={{
                  padding: "10px 12px",
                  background: "rgba(16, 185, 129, 0.08)",
                  borderRadius: 8,
                  fontSize: 11,
                  lineHeight: 1.5,
                  color: W(0.7),
                }}>
                  A IA vai coletar todos os campos abaixo durante a conversa. O card so sera criado no CRM quando <strong style={{ color: "#10b981" }}>todos os campos</strong> forem preenchidos.
                </div>
              </div>

              {/* Seletor de quadro */}
              {crmBoards.length > 1 && (
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Quadro CRM</span>
                    <Tooltip
                      title="Em qual quadro do CRM o card sera criado. Se nao selecionado, usa o quadro padrao."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  <div className={classes.configDropdown} style={{ maxHeight: 150 }}>
                    {crmBoards.map(function (board) {
                      var isSelected = selectedBoardId === board.id;
                      return (
                        <div
                          key={board.id}
                          className={classes.configDropdownItem}
                          onClick={function () { handleBoardChange(board.id); }}
                        >
                          <div
                            className={classes.configDropdownCheck + (isSelected ? " " + classes.configDropdownCheckActive : "")}
                          >
                            {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                          </div>
                          <span className={classes.configDropdownColor} style={{ background: board.color || "#5B7CFF" }} />
                          {board.name}
                          {board.isDefault && <span style={{ fontSize: 9, color: W(0.4), marginLeft: 4 }}>(padrao)</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Adicionar campo</span>
                  <Tooltip
                    title="Nome do campo que a IA precisa coletar. Ex: Nome completo, Telefone, Endereco, CPF, etc."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div className={classes.configInputRow}>
                  <input
                    className={classes.configInput}
                    value={crmFieldName}
                    onChange={function (e) { setCrmFieldName(e.target.value); }}
                    placeholder="ex: Nome completo"
                    onKeyDown={function (e) { if (e.key === "Enter") { e.preventDefault(); addField(); } }}
                  />
                  <button
                    className={classes.configAddBtn}
                    onClick={addField}
                    type="button"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>

              {fields.length > 0 && (
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Campos obrigatorios ({fields.length})</span>
                  </div>
                  <div className={classes.configDomainList}>
                    {fields.map(function (field) {
                      return (
                        <div key={field.id} className={classes.configDomainItem}>
                          <SquareKanban size={12} style={{ color: "#10b981", flexShrink: 0 }} />
                          <span className={classes.configDomainText}>{field.name}</span>
                          <span
                            className={classes.configDomainRemove}
                            onClick={function () { removeField(field.id); }}
                          >
                            <Trash2 size={12} />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {fields.length === 0 && (
                <div className={classes.configField}>
                  <div className={classes.configEmpty}>
                    Nenhum campo adicionado
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })()}

        {/* ═══ ATUALIZAR CARD CRM ═══ */}
        {cfg.type === "tool_crm_update" && (function () {
          var selectedBoardId = nodeData.boardId || null;
          return (
            <React.Fragment>
              <div className={classes.configField}>
                <div style={{
                  padding: "10px 12px",
                  background: "rgba(6, 182, 212, 0.08)",
                  borderRadius: 8,
                  fontSize: 11,
                  lineHeight: 1.5,
                  color: W(0.7),
                }}>
                  <strong style={{ color: "#06b6d4" }}>Atualizar Card</strong>
                  <br /><br />
                  Quando conectado ao agente, a IA detecta automaticamente quando o cliente corrige ou atualiza uma informacao que ja foi coletada.
                  <br /><br />
                  <strong>Exemplo:</strong> se a IA coletou o endereco "Rua A, 100" e depois o cliente diz "na verdade e Rua B, 200", a IA vai atualizar o campo no card do CRM automaticamente.
                  <br /><br />
                  Nao e necessario configurar nada — basta conectar este node ao agente.
                </div>
              </div>

              {/* Seletor de quadro */}
              {crmBoards.length > 1 && (
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Quadro CRM</span>
                  </div>
                  <div className={classes.configDropdown} style={{ maxHeight: 150 }}>
                    {crmBoards.map(function (board) {
                      var isSelected = selectedBoardId === board.id;
                      return (
                        <div
                          key={board.id}
                          className={classes.configDropdownItem}
                          onClick={function () { updateNodeData("boardId", board.id); }}
                        >
                          <div
                            className={classes.configDropdownCheck + (isSelected ? " " + classes.configDropdownCheckActive : "")}
                          >
                            {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                          </div>
                          <span className={classes.configDropdownColor} style={{ background: board.color || "#5B7CFF" }} />
                          {board.name}
                          {board.isDefault && <span style={{ fontSize: 9, color: W(0.4), marginLeft: 4 }}>(padrao)</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })()}

        {/* ═══ STATUS CARD CRM ═══ */}
        {cfg.type === "tool_crm_status" && (function () {
          var selectedBoardId = nodeData.boardId || null;
          var startColumn = nodeData.startColumn || null;
          var endColumn = nodeData.endColumn || null;
          var middleColumns = nodeData.middleColumns || [];
          var sortedStatuses = leadStatuses.slice().sort(function (a, b) { return (a.order || 0) - (b.order || 0); });

          var handleBoardChangeStatus = function (boardId) {
            updateNodeData("boardId", boardId);
            // Resetar colunas ao trocar de board
            updateNodeData("startColumn", null);
            updateNodeData("endColumn", null);
            updateNodeData("middleColumns", []);
            // Recarregar statuses do board selecionado
            var url = boardId ? "/lead-statuses?boardId=" + boardId : "/lead-statuses";
            api.get(url).then(function (res) { setLeadStatuses(res.data); }).catch(function () {});
          };

          var setStartColumn = function (id) {
            updateNodeData("startColumn", id);
          };
          var setEndColumn = function (id) {
            updateNodeData("endColumn", id);
          };
          var addMiddleColumn = function (id) {
            if (middleColumns.indexOf(id) !== -1) return;
            updateNodeData("middleColumns", middleColumns.concat(id));
          };
          var removeMiddleColumn = function (id) {
            updateNodeData("middleColumns", middleColumns.filter(function (c) { return c !== id; }));
          };

          var getColumnName = function (id) {
            var found = leadStatuses.find(function (s) { return s.id === id; });
            return found ? found.name : "?";
          };
          var getColumnColor = function (id) {
            var found = leadStatuses.find(function (s) { return s.id === id; });
            return found ? found.color : "#666";
          };

          // Available columns (excluding already selected)
          var usedIds = [startColumn, endColumn].concat(middleColumns).filter(Boolean);
          var availableColumns = sortedStatuses.filter(function (s) { return usedIds.indexOf(s.id) === -1; });

          return (
            <React.Fragment>
              <div className={classes.configField}>
                <div style={{
                  padding: "10px 12px",
                  background: "rgba(249, 115, 22, 0.08)",
                  borderRadius: 8,
                  fontSize: 11,
                  lineHeight: 1.5,
                  color: W(0.7),
                }}>
                  Defina a jornada de status que a IA vai seguir. Ela começa na <strong style={{ color: "#f97316" }}>coluna inicial</strong> e vai movendo o card ate a <strong style={{ color: "#f97316" }}>coluna de conclusao</strong>.
                </div>
              </div>

              {/* Seletor de quadro */}
              {crmBoards.length > 1 && (
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Quadro CRM</span>
                    <Tooltip
                      title="Selecione o quadro cujas colunas serao usadas para a jornada de status."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  <div className={classes.configDropdown} style={{ maxHeight: 150 }}>
                    {crmBoards.map(function (board) {
                      var isSelected = selectedBoardId === board.id;
                      return (
                        <div
                          key={board.id}
                          className={classes.configDropdownItem}
                          onClick={function () { handleBoardChangeStatus(board.id); }}
                        >
                          <div
                            className={classes.configDropdownCheck + (isSelected ? " " + classes.configDropdownCheckActive : "")}
                          >
                            {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                          </div>
                          <span className={classes.configDropdownColor} style={{ background: board.color || "#5B7CFF" }} />
                          {board.name}
                          {board.isDefault && <span style={{ fontSize: 9, color: W(0.4), marginLeft: 4 }}>(padrao)</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Coluna inicial */}
              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Coluna inicial</span>
                </div>
                <div className={classes.configDropdown} style={{ maxHeight: 150 }}>
                  {sortedStatuses.map(function (s) {
                    var isSelected = startColumn === s.id;
                    return (
                      <div
                        key={s.id}
                        className={classes.configDropdownItem}
                        onClick={function () { setStartColumn(s.id); }}
                      >
                        <div
                          className={classes.configDropdownCheck + (isSelected ? " " + classes.configDropdownCheckActive : "")}
                        >
                          {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                        </div>
                        <span className={classes.configDropdownColor} style={{ background: s.color || "#666" }} />
                        {s.name}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Colunas intermediarias */}
              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Colunas intermediarias</span>
                  <Tooltip
                    title="Opcional. Adicione colunas pelas quais o card deve passar antes da conclusao. A IA move o card conforme o progresso."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>

                {middleColumns.length > 0 && (
                  <div className={classes.configDomainList} style={{ marginBottom: 6 }}>
                    {middleColumns.map(function (colId, idx) {
                      return (
                        <div key={colId} className={classes.configDomainItem}>
                          <span className={classes.configDropdownColor} style={{ background: getColumnColor(colId), flexShrink: 0 }} />
                          <span className={classes.configDomainText}>{getColumnName(colId)}</span>
                          <span
                            className={classes.configDomainRemove}
                            onClick={function () { removeMiddleColumn(colId); }}
                          >
                            <Trash2 size={12} />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {availableColumns.length > 0 && (
                  <div className={classes.configDropdown} style={{ maxHeight: 120 }}>
                    {availableColumns.map(function (s) {
                      return (
                        <div
                          key={s.id}
                          className={classes.configDropdownItem}
                          onClick={function () { addMiddleColumn(s.id); }}
                        >
                          <Plus size={10} style={{ marginRight: 4, opacity: 0.5 }} />
                          <span className={classes.configDropdownColor} style={{ background: s.color || "#666" }} />
                          {s.name}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Coluna de conclusao */}
              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Coluna de conclusao</span>
                </div>
                <div className={classes.configDropdown} style={{ maxHeight: 150 }}>
                  {sortedStatuses.map(function (s) {
                    var isSelected = endColumn === s.id;
                    return (
                      <div
                        key={s.id}
                        className={classes.configDropdownItem}
                        onClick={function () { setEndColumn(s.id); }}
                      >
                        <div
                          className={classes.configDropdownCheck + (isSelected ? " " + classes.configDropdownCheckActive : "")}
                        >
                          {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                        </div>
                        <span className={classes.configDropdownColor} style={{ background: s.color || "#666" }} />
                        {s.name}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Visual da jornada */}
              {startColumn && endColumn && (
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Jornada do card</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: getColumnColor(startColumn), flexShrink: 0 }} />
                      <span style={{ color: W(0.8) }}>{getColumnName(startColumn)}</span>
                      <span style={{ color: W(0.3), fontSize: 9 }}>(inicio)</span>
                    </div>
                    {middleColumns.map(function (colId) {
                      return (
                        <div key={colId} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, paddingLeft: 12 }}>
                          <span style={{ color: W(0.3) }}>↓</span>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: getColumnColor(colId), flexShrink: 0 }} />
                          <span style={{ color: W(0.7) }}>{getColumnName(colId)}</span>
                        </div>
                      );
                    })}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, paddingLeft: middleColumns.length > 0 ? 12 : 0 }}>
                      {middleColumns.length > 0 && <span style={{ color: W(0.3) }}>↓</span>}
                      {middleColumns.length === 0 && <span style={{ color: W(0.3), marginRight: 2 }}>→</span>}
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: getColumnColor(endColumn), flexShrink: 0 }} />
                      <span style={{ color: W(0.8) }}>{getColumnName(endColumn)}</span>
                      <span style={{ color: W(0.3), fontSize: 9 }}>(conclusao)</span>
                    </div>
                  </div>
                </div>
              )}

              {leadStatuses.length === 0 && (
                <div className={classes.configField}>
                  <div className={classes.configEmpty}>
                    Nenhuma coluna CRM encontrada. Crie colunas em CRM &gt; Configuracoes.
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })()}
      </ConfigBody>

      <AiToolsGuideModal
        open={guideOpen}
        onClose={function () { setGuideOpen(false); }}
      />
      </div>
    </div>
  );
};

export default ConfigPanel;
